// NotificationService.js - Updated with financial notification support
import { getFirestoreInstance } from './firebaseHelper';
import { 
  collection, 
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  updateDoc,
  runTransaction,
  doc,
  onSnapshot,
  addDoc,
  getDoc,
  serverTimestamp,
  arrayRemove,
  arrayUnion,
  setDoc,
  writeBatch,
  deleteDoc,
  increment
} from 'firebase/firestore';
import { normalizeDate } from './dateUtils';
// Cache keys
const CACHE_KEYS = {
  GENERAL: 'cached_notifications_general',
  INVITATIONS: 'cached_notifications_invitations',
  BAND: 'cached_band_notifications_',
  LAST_UPDATED: 'notifications_last_updated',
};

// Add this constant separately
const RECOMMENDATION_CACHE_PREFIX = 'recommendationCache_';

// Notification Types
export const NOTIFICATION_TYPES = {
  // Band notification types
  BAND_CREATED: 'BAND_CREATED',
  ADMIN_ADDED: 'ADMIN_ADDED',
  ADMIN_REMOVED: 'ADMIN_REMOVED',
  USER_ADDED: 'USER_ADDED',
  USER_REMOVED: 'USER_REMOVED',
  MEMBER_REQUEST: 'MEMBER_REQUEST',
  REQUEST_ACCEPTED: 'REQUEST_ACCEPTED',
  REQUEST_REJECTED: 'REQUEST_REJECTED',
  BAND_UPDATED: 'BAND_UPDATED',
  BAND_DELETED: 'BAND_DELETED',

  // Tour/show notification types
  TOUR_INVITATION: 'TOUR_INVITATION',
  SHOW_INVITATION: 'SHOW_INVITATION',
  TOUR_DATE_REMOVAL: 'TOUR_DATE_REMOVAL',
  SHOW_REMOVAL: 'SHOW_REMOVAL',
  TASK_ASSIGNED: 'TASK_ASSIGNED',
  TOUR_DATE_RESCHEDULED: 'TOUR_DATE_RESCHEDULED',
  SHOW_RESCHEDULED: 'SHOW_RESCHEDULED',
  TOUR_DATE_CANCELLED: 'TOUR_DATE_CANCELLED',
  SHOW_CANCELLED: 'SHOW_CANCELLED',
  TOUR_DATE_UNCANCELLED: 'TOUR_DATE_UNCANCELLED',
  SHOW_UNCANCELLED: 'SHOW_UNCANCELLED',
  
  // New notification type for recommendation requests
  RECOMMENDATION_REQUEST: 'RECOMMENDATION_REQUEST',

  // Connection notifications
  CONNECTION_REQUEST: 'CONNECTION_REQUEST',

  // ORGANIZATION Manager notification types 
  ORGMANAGER_REQUEST: 'ORGMANAGER_REQUEST',
  ORGMANAGER_ACCEPTED: 'ORGMANAGER_ACCEPTED', 
  ORGMANAGER_REJECTED: 'ORGMANAGER_REJECTED',
  ORGMANAGER_REMOVAL: 'ORGMANAGER_REMOVAL',
  
  // ORGANIZATION Artist notification types
  ORGARTIST_REQUEST: 'ORGARTIST_REQUEST',
  ORGARTIST_ACCEPTED: 'ORGARTIST_ACCEPTED',
  ORGARTIST_REJECTED: 'ORGARTIST_REJECTED',
  ORGARTIST_REMOVAL: 'ORGARTIST_REMOVAL',

  ASSOCIATION_REQUEST: 'ASSOCIATION_REQUEST',
  ASSOCIATION_ACCEPTED: 'ASSOCIATION_ACCEPTED',
  ASSOCIATION_REJECTED: 'ASSOCIATION_REJECTED',
  ASSOCIATION_REMOVED: 'ASSOCIATION_REMOVED',
  BAND_ASSOCIATION_REQUEST: 'BAND_ASSOCIATION_REQUEST',
  BAND_ASSOCIATION_ACCEPTED: 'BAND_ASSOCIATION_ACCEPTED',
  BAND_ASSOCIATION_REJECTED: 'BAND_ASSOCIATION_REJECTED',

  // Endorsement notification types
  ENDORSEMENT_REQUEST: 'ENDORSEMENT_REQUEST',
  ENDORSEMENT_ACCEPTED: 'ENDORSEMENT_ACCEPTED',
  ENDORSEMENT_REJECTED: 'ENDORSEMENT_REJECTED',
  
  // Financial notification types
  EARNING_RECORDED: 'EARNING_RECORDED',
  EXPENSE_OWED: 'EXPENSE_OWED',
  EXPENSE_SETTLED: 'EXPENSE_SETTLED',

  ORGANIZATION_STAFF_REMOVAL: 'ORGANIZATION_STAFF_REMOVAL',
  STAFF_REQUEST: 'STAFF_REQUEST',
  STAFF_ACCEPTED: 'STAFF_ACCEPTED',
  STAFF_REJECTED: 'STAFF_REJECTED',
  STAFF_REMOVAL: 'STAFF_REMOVAL',
  
  // Artist-Manager notification types
  ARTIST_MANAGER_REMOVAL: 'ARTIST_MANAGER_REMOVAL',
  MANAGER_ARTIST_REMOVAL: 'MANAGER_ARTIST_REMOVAL',

  ARTIST_MOVED_TO_PAST: 'ARTIST_MOVED_TO_PAST',
BAND_MOVED_TO_PAST: 'BAND_MOVED_TO_PAST',

MANAGER_REQUEST: 'MANAGER_REQUEST',
PAST_MANAGER_REQUEST: 'PAST_MANAGER_REQUEST',
MANAGER_REMOVAL: 'MANAGER_REMOVAL',
MANAGER_MOVED_TO_PAST: 'MANAGER_MOVED_TO_PAST',

// Make sure these are defined

PAST_ORGARTIST_REQUEST: 'PAST_ORGARTIST_REQUEST',
ORGBAND_REQUEST: 'ORGBAND_REQUEST',
PAST_ORGBAND_REQUEST: 'PAST_ORGBAND_REQUEST',

NEW_FEEDBACK: 'NEW_FEEDBACK',
FEEDBACK_REPLY: 'FEEDBACK_REPLY',
FEEDBACK_UPDATED: 'FEEDBACK_UPDATED',
NEW_REVIEW: 'NEW_REVIEW',
REVIEW_REPLY: 'REVIEW_REPLY',
REVIEW_UPDATED: 'REVIEW_UPDATED',

// vendorstaff.js notification types 

VENDOR_STAFF_REQUEST: 'VENDOR_STAFF_REQUEST',
PAST_VENDOR_STAFF_REQUEST: 'PAST_VENDOR_STAFF_REQUEST',
VENDOR_STAFF_ACCEPTED: 'VENDOR_STAFF_ACCEPTED',
VENDOR_STAFF_REJECTED: 'VENDOR_STAFF_REJECTED',
VENDOR_STAFF_REMOVAL: 'VENDOR_STAFF_REMOVAL',
PAST_VENDOR_STAFF_REMOVAL: 'PAST_VENDOR_STAFF_REMOVAL',
VENDOR_STAFF_PERMISSIONS_UPDATED: 'VENDOR_STAFF_PERMISSIONS_UPDATED',
VENDOR_STAFF_MOVED_TO_PAST: 'VENDOR_STAFF_MOVED_TO_PAST',

// Associated Vendors notification types
ASSOCIATED_VENDOR_REQUEST: 'ASSOCIATED_VENDOR_REQUEST',
ASSOCIATED_VENDOR_ACCEPTED: 'ASSOCIATED_VENDOR_ACCEPTED',
ASSOCIATED_VENDOR_REJECTED: 'ASSOCIATED_VENDOR_REJECTED',
ASSOCIATED_VENDOR_REMOVED: 'ASSOCIATED_VENDOR_REMOVED',
PAST_ASSOCIATED_VENDOR_REQUEST: 'PAST_ASSOCIATED_VENDOR_REQUEST',
PAST_ASSOCIATED_VENDOR_ACCEPTED: 'PAST_ASSOCIATED_VENDOR_ACCEPTED',
PAST_ASSOCIATED_VENDOR_REJECTED: 'PAST_ASSOCIATED_VENDOR_REJECTED',
PAST_ASSOCIATED_VENDOR_REMOVED: 'PAST_ASSOCIATED_VENDOR_REMOVED',
ASSOCIATED_VENDOR_MOVED_TO_PAST: 'ASSOCIATED_VENDOR_MOVED_TO_PAST',

};

// User-specific cache management functions
const getUserSpecificCacheKey = (userId, baseKey) => {
  return `${baseKey}_${userId}`;
};

const saveToCache = (key, data, userId = null) => {
  try {
    const cacheKey = userId ? getUserSpecificCacheKey(userId, key) : key;
    localStorage.setItem(cacheKey, JSON.stringify({
      data,
      timestamp: Date.now(),
      userId: userId
    }));
  } catch (error) {
    console.error('Error saving to cache:', error);
    clearOldItems();
  }
};

const getFromCache = (key, userId = null) => {
  try {
    const cacheKey = userId ? getUserSpecificCacheKey(userId, key) : key;
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    
    const parsedCache = JSON.parse(cached);
    
    // Verify the cache belongs to the current user
    if (userId && parsedCache.userId && parsedCache.userId !== userId) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    return parsedCache.data;
  } catch (error) {
    console.error('Error reading from cache:', error);
    return null;
  }
};

const clearOldItems = () => {
  try {
    const keys = Object.keys(localStorage);
    const itemsWithTimestamp = [];
    
    for (const key of keys) {
      try {
        const item = localStorage.getItem(key);
        const parsed = JSON.parse(item);
        if (parsed && parsed.timestamp) {
          itemsWithTimestamp.push({ key, timestamp: parsed.timestamp });
        }
      } catch (e) {
        // Skip items that can't be parsed
      }
    }
    
    itemsWithTimestamp.sort((a, b) => a.timestamp - b.timestamp);
    const itemsToRemove = Math.max(1, Math.floor(itemsWithTimestamp.length * 0.2));
    
    for (let i = 0; i < itemsToRemove; i++) {
      if (itemsWithTimestamp[i]) {
        localStorage.removeItem(itemsWithTimestamp[i].key);
      }
    }
  } catch (error) {
    console.error('Error clearing old cache items:', error);
  }
};

/* ====== USER NOTIFICATIONS FUNCTIONS (Updated for Individual Documents) ====== */

// Get notifications with individual document approach
export async function getNotifications(userId, type = 'general', lastDoc = null, pageSize = 8, forceFetch = false) {
  const db = getFirestoreInstance();
  const cacheKey = type === 'general' ? CACHE_KEYS.GENERAL : CACHE_KEYS.INVITATIONS;
  
  try {
    // Initial load - check cache first if not forcing fetch and not requesting more
    if (!forceFetch && !lastDoc) {
      const cachedData = getFromCache(cacheKey, userId);
      
      if (cachedData) {
        return {
          items: cachedData.items.slice(0, pageSize),
          hasMore: cachedData.items.length > pageSize,
          lastChunk: cachedData.items.length > pageSize ? cachedData.items[pageSize - 1] : null,
          allItems: cachedData.items
        };
      }
    }
    
    const collectionName = type === 'general' ? 'notifications' : 'eventInvitations';
    
    let notificationsQuery = query(
      collection(db, 'users', userId, collectionName),
      orderBy('timestamp', 'desc'),
      limit(pageSize)
    );
    
    // Add pagination if lastDoc is provided
    if (lastDoc && lastDoc.timestamp) {
      notificationsQuery = query(
        collection(db, 'users', userId, collectionName),
        orderBy('timestamp', 'desc'),
        startAfter(lastDoc.timestamp),
        limit(pageSize)
      );
    }
    
    const snapshot = await getDocs(notificationsQuery);
    
    if (snapshot.empty) {
      return {
        items: [],
        hasMore: false,
        lastChunk: null,
        allItems: []
      };
    }
    
    const notifications = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      docRef: doc.ref
    }));
    
    // Cache only the first page
    if (!lastDoc) {
      saveToCache(cacheKey, {
        items: notifications,
        timestamp: Date.now()
      }, userId);
    }
    
    return {
      items: notifications,
      hasMore: notifications.length === pageSize,
      lastChunk: notifications.length > 0 ? notifications[notifications.length - 1] : null,
      allItems: notifications
    };
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
}

// Function to mark notifications as read (individual documents)
export async function markAsRead(userId, notificationIds, type = 'general') {
  const db = getFirestoreInstance();
  try {
    const collectionName = type === 'general' ? 'notifications' : 'eventInvitations';
    const batch = writeBatch(db);

    // Update each notification document individually
    for (const notificationId of notificationIds) {
      const notificationRef = doc(db, 'users', userId, collectionName, notificationId);
      batch.update(notificationRef, { read: true });
    }

    await batch.commit();

    // Update the cache
    const cacheKey = type === 'general' ? CACHE_KEYS.GENERAL : CACHE_KEYS.INVITATIONS;
    const cachedData = getFromCache(cacheKey, userId);
    
    if (cachedData && cachedData.items) {
      const updatedItems = cachedData.items.map(item => {
        if (notificationIds.includes(item.id)) {
          return { ...item, read: true };
        }
        return item;
      });
      
      saveToCache(cacheKey, {
        ...cachedData,
        items: updatedItems,
        timestamp: Date.now()
      }, userId);
    }

    return true;
  } catch (error) {
    console.error('Error marking notifications as read:', error);
    throw error;
  }
}

// Function to handle new notification creation (individual documents)
export async function createNotification(userId, type = 'general', notificationData, currentUserId = null) {
  const db = getFirestoreInstance();
  try {
    const collectionName = type === 'general' ? 'notifications' : 'eventInvitations';
    
    const newNotification = {
      ...notificationData,
      id: crypto.randomUUID(),
      timestamp: Timestamp.now(),
      read: false,
      status: notificationData.status || 'pending'
    };

    // Create individual document with the notification ID as document ID
    const notificationRef = doc(db, 'users', userId, collectionName, newNotification.id);
    await setDoc(notificationRef, newNotification);

    // Update cache only if this is for the current user
    if (currentUserId === userId) {
      const cacheKey = type === 'general' ? CACHE_KEYS.GENERAL : CACHE_KEYS.INVITATIONS;
      const cachedData = getFromCache(cacheKey, userId);
      
      if (cachedData && cachedData.items) {
        saveToCache(cacheKey, {
          ...cachedData,
          items: [newNotification, ...cachedData.items],
          timestamp: Date.now()
        }, userId);
      }
    }

    return newNotification;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
}

/* ====== BAND NOTIFICATIONS FUNCTIONS ====== */

// Function to create a notification in a band's notifications collection
export async function createBandNotification(notificationData) {
  try {
    const { 
      bandId, 
      type, 
      title, 
      message, 
      recipientIds, 
      actionBy,
      data = {}
    } = notificationData;

    if (!bandId) {
      console.error('No bandId provided for band notification');
      return null;
    }

    const db = getFirestoreInstance();
    
    // Create the notification in the band's notifications collection
    const notificationId = crypto.randomUUID();
    const notification = {
      id: notificationId,
      type,
      title,
      message,
      timestamp: serverTimestamp(),
      actionBy: {
        userId: actionBy.userId,
        _displayData: {
          fullName: actionBy.fullName,
          userName: actionBy.userName,
          profilePictureUrl: actionBy.profilePictureUrl
        }
      },
      data: {
        ...data,
        bandId,
        _displayData: {
          bandName: notificationData.bandName
        }
      },
      readBy: {}
    };

    // Add to the band's notifications collection
    await addDoc(collection(db, 'bands', bandId, 'notifications'), notification);

    // Create a reference to this notification in each recipient's inbox
    if (recipientIds && Array.isArray(recipientIds) && recipientIds.length > 0) {
      for (const recipientUserId of recipientIds) {
        if (recipientUserId === actionBy.userId) continue;
        
        await createNotification(recipientUserId, 'general', {
          type,
          title,
          message,
          bandNotificationId: notificationId,
          data: {
            bandId,
            actionBy: {
              userId: actionBy.userId
            },
            _displayData: {
              bandName: notificationData.bandName,
              actionByName: actionBy.fullName,
              actionByProfilePicture: actionBy.profilePictureUrl
            }
          }
        });
      }
    }

    return true;
  } catch (error) {
    console.error('Error creating band notification:', error);
    return null;
  }
}

// Function to get band notifications
export async function getBandNotifications(bandId, lastItem = null, pageSize = 8, forceFetch = false) {
  const db = getFirestoreInstance();
  const cacheKey = `${CACHE_KEYS.BAND}${bandId}`;
  
  try {
    if (!forceFetch && !lastItem) {
      const cachedData = getFromCache(cacheKey);
      
      if (cachedData) {
        return {
          items: cachedData.items.slice(0, pageSize),
          hasMore: cachedData.items.length > pageSize,
          lastItem: cachedData.items.length > pageSize ? cachedData.items[pageSize - 1] : null
        };
      }
    }
    
    let notificationsQuery = query(
      collection(db, 'bands', bandId, 'notifications'),
      orderBy('timestamp', 'desc'),
      limit(pageSize)
    );
    
    if (lastItem && lastItem.timestamp) {
      notificationsQuery = query(
        collection(db, 'bands', bandId, 'notifications'),
        orderBy('timestamp', 'desc'),
        startAfter(lastItem.timestamp),
        limit(pageSize)
      );
    }
    
    const snapshot = await getDocs(notificationsQuery);
    
    const notifications = snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
      docRef: doc.ref
    }));
    
    if (!lastItem) {
      saveToCache(cacheKey, {
        items: notifications,
        timestamp: Date.now()
      });
    }
    
    return {
      items: notifications,
      hasMore: notifications.length === pageSize,
      lastItem: notifications.length > 0 ? notifications[notifications.length - 1] : null
    };
  } catch (error) {
    console.error('Error fetching band notifications:', error);
    throw error;
  }
}

// Mark a band notification as read by a user
export async function markBandNotificationAsRead(bandId, notificationId, userId) {
  const db = getFirestoreInstance();
  
  try {
    const notificationsQuery = query(
      collection(db, 'bands', bandId, 'notifications'),
      where('id', '==', notificationId),
      limit(1)
    );
    
    const snapshot = await getDocs(notificationsQuery);
    
    if (snapshot.empty) {
      throw new Error('Notification not found');
    }
    
    const notificationDoc = snapshot.docs[0];
    const notificationData = notificationDoc.data();
    
    const readBy = notificationData.readBy || {};
    readBy[userId] = Timestamp.now();
    
    await updateDoc(notificationDoc.ref, { readBy });
    
    // Update the cache
    const cacheKey = `${CACHE_KEYS.BAND}${bandId}`;
    const cachedData = getFromCache(cacheKey);
    
    if (cachedData && cachedData.items) {
      const updatedItems = cachedData.items.map(item => {
        if (item.id === notificationId) {
          const updatedReadBy = {...(item.readBy || {})};
          updatedReadBy[userId] = Timestamp.now();
          return { ...item, readBy: updatedReadBy };
        }
        return item;
      });
      
      saveToCache(cacheKey, {
        ...cachedData,
        items: updatedItems,
        timestamp: Date.now()
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error marking band notification as read:', error);
    throw error;
  }
}

// Check if a band notification is read by a user
export function isBandNotificationRead(notification, userId) {
  if (!notification || !notification.readBy || !userId) {
    return false;
  }
  
  return !!notification.readBy[userId];
}

// Get unread band notifications count for a user
export function getUnreadBandNotificationsCount(notifications, userId) {
  if (!notifications || !Array.isArray(notifications) || !userId) {
    return 0;
  }
  
  return notifications.filter(notification => !isBandNotificationRead(notification, userId)).length;
}

// Set up real-time notifications listener (updated for individual documents)
export function setupNotificationsListener(userId, onNotificationReceived) {
  if (!userId) return () => {};
  
  const db = getFirestoreInstance();
  
  // Keep track of recently processed notification IDs to prevent duplicates
  const recentlyProcessedIds = new Set();

  // Listen for general notifications
  const generalQuery = query(
    collection(db, 'users', userId, 'notifications'),
    orderBy('timestamp', 'desc'),
    limit(8)
  );

  // Listen for tour invitations
  const invitationsQuery = query(
    collection(db, 'users', userId, 'eventInvitations'),
    orderBy('timestamp', 'desc'),
    limit(8)
  );

  // Set up listeners
  const unsubscribeGeneral = onSnapshot(generalQuery, snapshot => {
    const changes = snapshot.docChanges();
    const newNotifications = [];
    
    changes.forEach(change => {
      if (change.type === 'added') {
        const notification = {
          id: change.doc.id,
          ...change.doc.data()
        };
        
        // Check if this is truly a new notification (created in the last minute)
        // AND hasn't been processed already
        const now = Date.now();
        const notificationTime = notification.timestamp?.seconds * 1000 || 0;
        
        if (now - notificationTime < 60000 && !recentlyProcessedIds.has(notification.id)) {
          // Add to recently processed set to prevent duplicates
          recentlyProcessedIds.add(notification.id);
          newNotifications.push(notification);
          
          // Clean up set after 2 minutes to prevent memory leaks
          setTimeout(() => {
            recentlyProcessedIds.delete(notification.id);
          }, 120000);
        }
      }
    });
    
    if (newNotifications.length > 0) {
      // Update cache
      const cachedData = getFromCache(CACHE_KEYS.GENERAL, userId);
      if (cachedData) {
        // Deduplicate cached notifications
        const existingIds = new Set(cachedData.items.map(item => item.id));
        const uniqueNewItems = newNotifications.filter(item => !existingIds.has(item.id));
        
        if (uniqueNewItems.length > 0) {
          const updatedItems = [...uniqueNewItems, ...cachedData.items];
          saveToCache(CACHE_KEYS.GENERAL, {
            ...cachedData,
            items: updatedItems,
            timestamp: Date.now()
          }, userId);
        }
      }
      
      // Notify about changes
      if (onNotificationReceived && newNotifications.length > 0) {
        onNotificationReceived({
          type: 'general',
          newNotifications
        });
      }
    }
  });

  const unsubscribeInvitations = onSnapshot(invitationsQuery, snapshot => {
    const changes = snapshot.docChanges();
    const newInvitations = [];
    
    changes.forEach(change => {
      if (change.type === 'added') {
        const invitation = {
          id: change.doc.id,
          ...change.doc.data()
        };
        
        // Check if this is truly a new invitation (created in the last minute)
        // AND hasn't been processed already
        const now = Date.now();
        const invitationTime = invitation.timestamp?.seconds * 1000 || 0;
        
        if (now - invitationTime < 60000 && !recentlyProcessedIds.has(invitation.id)) {
          // Add to recently processed set to prevent duplicates
          recentlyProcessedIds.add(invitation.id);
          newInvitations.push(invitation);
          
          // Clean up set after 2 minutes to prevent memory leaks
          setTimeout(() => {
            recentlyProcessedIds.delete(invitation.id);
          }, 120000);
        }
      }
    });
    
    if (newInvitations.length > 0) {
      // Update cache
      const cachedData = getFromCache(CACHE_KEYS.INVITATIONS, userId);
      if (cachedData) {
        // Deduplicate cached invitations
        const existingIds = new Set(cachedData.items.map(item => item.id));
        const uniqueNewItems = newInvitations.filter(item => !existingIds.has(item.id));
        
        if (uniqueNewItems.length > 0) {
          const updatedItems = [...uniqueNewItems, ...cachedData.items];
          saveToCache(CACHE_KEYS.INVITATIONS, {
            ...cachedData,
            items: updatedItems,
            timestamp: Date.now()
          }, userId);
        }
      }
      
      // Notify about changes
      if (onNotificationReceived && newInvitations.length > 0) {
        onNotificationReceived({
          type: 'invitations',
          newInvitations
        });
      }
    }
  });

  return () => {
    unsubscribeGeneral();
    unsubscribeInvitations();
  };
}

// Setup real-time band notifications listener
export function setupBandNotificationsListener(bandId, userId, onNotificationReceived) {
  if (!bandId || !userId) return () => {};
  
  const db = getFirestoreInstance();
  
  const notificationsQuery = query(
    collection(db, 'bands', bandId, 'notifications'),
    orderBy('timestamp', 'desc'),
    limit(8)
  );
  
  const unsubscribe = onSnapshot(notificationsQuery, snapshot => {
    const cacheKey = `${CACHE_KEYS.BAND}${bandId}`;
    let cachedData = getFromCache(cacheKey) || { items: [] };
    let cachedItems = new Map(cachedData.items.map(item => [item.id, item]));
    
    const changes = snapshot.docChanges();
    let hasNewNotifications = false;
    let newNotifications = [];
    
    changes.forEach(change => {
      const data = {
        id: change.doc.id,
        ...change.doc.data(),
        docRef: change.doc.ref
      };
      
      if (change.type === 'added' || change.type === 'modified') {
        const existingNotification = cachedItems.get(data.id);
        const isNew = !existingNotification;
        const isUpdated = existingNotification && 
          JSON.stringify(existingNotification) !== JSON.stringify(data);
        
        if (isNew || isUpdated) {
          cachedItems.set(data.id, data);
          
          if (isNew) {
            // Check if this is truly a new notification (created in the last minute)
            const now = Date.now();
            const notificationTime = data.timestamp?.seconds * 1000 || 0;
            
            if (now - notificationTime < 60000) { // Within the last minute
              hasNewNotifications = true;
              newNotifications.push(data);
            }
          }
        }
      }
      
      if (change.type === 'removed') {
        cachedItems.delete(data.id);
      }
    });
    
    // Update cache with all items
    const updatedItems = Array.from(cachedItems.values())
      .sort((a, b) => {
        const timeA = a.timestamp?.seconds || 0;
        const timeB = b.timestamp?.seconds || 0;
        return timeB - timeA;
      });
    
    saveToCache(cacheKey, {
      items: updatedItems,
      timestamp: Date.now()
    });
    
    // Notify about new notifications
    if (hasNewNotifications && onNotificationReceived) {
      const unreadNewNotifications = newNotifications.filter(notification => 
        !isBandNotificationRead(notification, userId)
      );
      
      if (unreadNewNotifications.length > 0) {
        onNotificationReceived({
          type: 'band',
          bandId,
          newNotifications: unreadNewNotifications
        });
      }
    }
  });
  
  return unsubscribe;
}

// Helper function to send push notifications via Expo
async function sendExpoPushNotification(expoPushToken, title, body, data) {
  try {
    const message = {
      to: expoPushToken,
      sound: 'default',
      title,
      body,
      data,
      _displayInForeground: true,
    };

    const response = await fetch('https://exp.host/--/api/v2/push/send', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Accept-encoding': 'gzip, deflate',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(message),
    });

    console.log('Push notification sent:', {
      status: response.status,
      notification: { title, body }
    });
    return true;
  } catch (error) {
    console.error('Error sending Expo push notification:', error);
    return false;
  }
}

// Send Push Notification for Band Events to specific users
export const sendBandPushNotification = async (recipientId, notification) => {
  try {
    const db = getFirestoreInstance();
    const userDoc = await getDoc(doc(db, 'users', recipientId));
    
    if (!userDoc.exists()) {
      console.warn(`User not found for notification: ${recipientId}`);
      return false;
    }
    
    const userData = userDoc.data();
    const expoPushToken = userData.expoPushToken;
    
    if (!expoPushToken) {
      console.warn(`No push token for user: ${recipientId}`);
      return false;
    }
    
    await sendExpoPushNotification(
      expoPushToken,
      notification.title,
      notification.message,
      {
        type: notification.type,
        notificationId: notification.id,
        ...notification.data
      }
    );
    
    return true;
  } catch (error) {
    console.error('Error sending band push notification:', error);
    return false;
  }
};

// Synchronize band notification read status
export async function syncBandNotificationReadStatus(bandId, notificationId, userId) {
  const db = getFirestoreInstance();
  
  try {
    await markBandNotificationAsRead(bandId, notificationId, userId);
    
    // Find and mark the corresponding user notification as read
    const userNotificationsQuery = query(
      collection(db, 'users', userId, 'notifications'),
      where('bandNotificationId', '==', notificationId),
      limit(1)
    );
    
    const snapshot = await getDocs(userNotificationsQuery);
    
    if (!snapshot.empty) {
      const userNotificationDoc = snapshot.docs[0];
      await updateDoc(userNotificationDoc.ref, { read: true });
      
      // Update cache
      const cachedData = getFromCache(CACHE_KEYS.GENERAL, userId);
      if (cachedData && cachedData.items) {
        const updatedItems = cachedData.items.map(item => {
          if (item.bandNotificationId === notificationId) {
            return { ...item, read: true };
          }
          return item;
        });
        
        saveToCache(CACHE_KEYS.GENERAL, {
          ...cachedData,
          items: updatedItems,
          timestamp: Date.now()
        }, userId);
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error syncing notification read status:', error);
    throw error;
  }
}

/**
 * Send an event invitation notification to a user
 * Works with both tours and shows
 * @param {string} userId - The user to send the notification to
 * @param {object} notificationData - The invitation data
 * @returns {Promise<boolean>} Success status
 */
export const sendEventInvitationNotification = async (userId, notificationData) => {
  try {
    // Get user's map privacy setting first
    let mapPrivacy = "public";  // Default
    try {
      const db = getFirestoreInstance();
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        mapPrivacy = userDoc.data().mapPrivacy || "public";
      }
    } catch (err) {
      console.error('Error getting user map privacy:', err);
    }
    
    // Determine the notification type based on event type
    const notificationType = notificationData.eventType === 'show' 
      ? NOTIFICATION_TYPES.SHOW_INVITATION 
      : NOTIFICATION_TYPES.TOUR_INVITATION;
    
    // Determine message based on event type
    const message = `You've been invited to join ${notificationData.eventType === 'show' ? 'the show' : 'the tour'} "${notificationData.tourName || notificationData.showName}"`;
    
    // Create a notification in the 'invitations' category for the user
    await createNotification(userId, 'invitations', {
      type: notificationType,
      title: `New ${notificationData.eventType === 'show' ? 'Show' : 'Tour'} Invitation`,
      message,
      status: 'pending', // Make sure status is set to pending
      mapPrivacy, // Include the user's map privacy setting
      ...notificationData,
      data: {
        ...notificationData.data,
        eventId: notificationData.eventId || notificationData.tourId,
        showId: notificationData.eventType === 'show' ? (notificationData.eventId || notificationData.tourId) : null,
        eventType: notificationData.eventType || 'tour',
        invitationId: notificationData.invitationId,
        creatorId: notificationData.createdBy?.userId,
        
        _displayData: {
          tourName: notificationData.tourName,
          showName: notificationData.eventType === 'show' ? notificationData.showName || notificationData.tourName : null,
          actName: notificationData.actName,
          creatorName: notificationData.createdBy?.fullName,
          creatorPhoto: notificationData.createdBy?.profilePictureUrl
        }
      }
    });
    
    // Send push notification if available
    const db = getFirestoreInstance();
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const expoPushToken = userData.expoPushToken;
      
      if (expoPushToken) {
        await sendExpoPushNotification(
          expoPushToken,
          `New ${notificationData.eventType === 'show' ? 'Show' : 'Tour'} Invitation`,
          message,
          {
            type: notificationType,
            eventId: notificationData.eventId || notificationData.tourId,
            eventType: notificationData.eventType || 'tour',
            invitationId: notificationData.invitationId
          }
        );
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error sending event invitation notification:', error);
    return false;
  }
};

/**
 * Function to send notification when removed from tour/show
 * @param {string} userId - The user to send notification to
 * @param {object} notificationData - Data about the removal
 * @returns {Promise<boolean>} Success status
 */
export const sendEventRemovalNotification = async (userId, notificationData) => {
  try {
    // Determine the notification type based on event type
    const notificationType = notificationData.eventType === 'show' 
      ? NOTIFICATION_TYPES.SHOW_REMOVAL 
      : NOTIFICATION_TYPES.TOUR_DATE_REMOVAL;
    
    // Determine message based on event type
    const message = notificationData.eventType === 'show'
      ? `You've been removed from the show "${notificationData.tourName || notificationData.showName}"`
      : `You've been removed from ${notificationData.dates.length} date(s) in ${notificationData.tourName}`;
    
    await createNotification(userId, 'general', {
      type: notificationType,
      title: notificationData.eventType === 'show' ? 'Removed from Show' : 'Removed from Tour Dates',
      message,
      data: {
        eventId: notificationData.eventId || notificationData.tourId,
        eventType: notificationData.eventType || 'tour',
        _displayData: {
          tourName: notificationData.tourName,
          showName: notificationData.eventType === 'show' ? notificationData.showName || notificationData.tourName : null,
          dates: notificationData.dates
        }
      }
    });
    
    // Send push notification if available
    const db = getFirestoreInstance();
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const expoPushToken = userData.expoPushToken;
      
      if (expoPushToken) {
        await sendExpoPushNotification(
          expoPushToken,
          notificationData.eventType === 'show' ? 'Removed from Show' : 'Removed from Tour Dates',
          message,
          {
            type: notificationType,
            eventId: notificationData.eventId || notificationData.tourId,
            eventType: notificationData.eventType || 'tour'
          }
        );
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error sending event removal notification:', error);
    return false;
  }
};

/**
 * Function to send notification when tour date/show status changes (cancelled or uncancelled)
 * Works with both tours and shows
 * @param {object} event - The tour or show object
 * @param {object} eventDate - The specific date object
 * @param {boolean} isCancelled - Whether the event is being cancelled or uncancelled
 * @param {object} actorData - Information about the user making the change
 * @returns {Promise<boolean>} Success status
 */
export const sendEventDateStatusNotification = async (event, eventDate, isCancelled, actorData) => {
  try {
    const recipients = [];
    const eventType = event.type || 'tour';
    const eventId = event.id;
    
    // For tour, collect recipients from assigned dates
    if (eventType === 'tour') {
      // Check assignedUsers subcollection first if available
      try {
        const db = getFirestoreInstance();
        const assignedUsersSnapshot = await getDocs(collection(db, 'tours', eventId, 'assignedUsers'));
        
        assignedUsersSnapshot.forEach(doc => {
          const userData = doc.data();
          if (userData.userId && userData.assignedDates?.some(d => d.dateDocId === eventDate.id)) {
            recipients.push(userData.userId);
          }
        });
      } catch (error) {
        console.error('Error fetching assignedUsers:', error);
        
        // Fallback to traditional fields if assignedUsers collection fails
        event.artistDetails?.forEach(artist => {
          if (artist.assignedDates?.some(d => d.dateDocId === eventDate.id)) {
            recipients.push(artist.userId);
          }
        });
        
        event.crewDetails?.forEach(crew => {
          if (crew.assignedDates?.some(d => d.dateDocId === eventDate.id)) {
            recipients.push(crew.userId);
          }
        });
      }
    } else {
      // For shows, collect recipients from assignedUsers subcollection
      try {
        const db = getFirestoreInstance();
        const assignedUsersSnapshot = await getDocs(collection(db, 'shows', eventId, 'assignedUsers'));
        
        assignedUsersSnapshot.forEach(doc => {
          const userData = doc.data();
          if (userData.userId) {
            recipients.push(userData.userId);
          }
        });
      } catch (error) {
        console.error('Error fetching show assignedUsers:', error);
      }
    }
    
    const uniqueRecipients = [...new Set(recipients)];
    const filteredRecipients = uniqueRecipients.filter(id => id !== actorData.userId);
    
    // Determine notification type based on event type and action
    const notificationType = eventType === 'show' 
      ? (isCancelled ? NOTIFICATION_TYPES.SHOW_CANCELLED : NOTIFICATION_TYPES.SHOW_UNCANCELLED)
      : (isCancelled ? NOTIFICATION_TYPES.TOUR_DATE_CANCELLED : NOTIFICATION_TYPES.TOUR_DATE_UNCANCELLED);
    
    // Create title and message based on event type
    const title = isCancelled
      ? (eventType === 'show' ? 'Show Cancelled' : 'Tour Date Cancelled')
      : (eventType === 'show' ? 'Show Reinstated' : 'Tour Date Reinstated');
      
    const message = isCancelled
      ? (eventType === 'show' 
          ? `The show "${event.showName || event.tourName}" has been cancelled` 
          : `A date for ${event.tourName} has been cancelled`)
      : (eventType === 'show'
          ? `The show "${event.showName || event.tourName}" has been reinstated`
          : `A date for ${event.tourName} has been reinstated`);
    
    for (const userId of filteredRecipients) {
      await createNotification(userId, 'general', {
        type: notificationType,
        title,
        message,
        data: {
          eventId: eventId,
          eventType: eventType,
          eventDateId: eventDate.id,
          actorId: actorData.userId,
          _displayData: {
            tourName: event.tourName,
            showName: eventType === 'show' ? event.showName || event.tourName : null,
            eventDate: eventDate.date,
            actorName: actorData.fullName,
            actorPhoto: actorData.profilePictureUrl
          }
        }
      });
      
      // Send push notification if available
      const db = getFirestoreInstance();
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const expoPushToken = userData.expoPushToken;
        
        if (expoPushToken) {
          await sendExpoPushNotification(
            expoPushToken,
            title,
            message,
            {
              type: notificationType,
              eventId: eventId,
              eventType: eventType,
              eventDateId: eventDate.id
            }
          );
        }
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error sending event date status notification:', error);
    return false;
  }
};

/**
 * Function to send notification when tour date/show is rescheduled
 * Works with both tours and shows
 * @param {object} event - The tour or show object
 * @param {object} eventDate - The specific date object
 * @param {string} oldDate - The previous date string
 * @param {string} newDate - The new date string
 * @param {object} actorData - Information about the user making the change
 * @returns {Promise<boolean>} Success status
 */
export const sendEventDateRescheduledNotification = async (event, eventDate, oldDate, newDate, actorData) => {
  try {
    const recipients = [];
    const eventType = event.type || 'tour';
    const eventId = event.id;
    
    // For tour, collect recipients from assigned dates
    if (eventType === 'tour') {
      // Check assignedUsers subcollection first if available
      try {
        const db = getFirestoreInstance();
        const assignedUsersSnapshot = await getDocs(collection(db, 'tours', eventId, 'assignedUsers'));
        
        assignedUsersSnapshot.forEach(doc => {
          const userData = doc.data();
          if (userData.userId && userData.assignedDates?.some(d => d.dateDocId === eventDate.id)) {
            recipients.push(userData.userId);
          }
        });
      } catch (error) {
        console.error('Error fetching assignedUsers:', error);
        
        // Fallback to traditional fields if assignedUsers collection fails
        event.artistDetails?.forEach(artist => {
          if (artist.assignedDates?.some(d => d.dateDocId === eventDate.id)) {
            recipients.push(artist.userId);
          }
        });
        
        event.crewDetails?.forEach(crew => {
          if (crew.assignedDates?.some(d => d.dateDocId === eventDate.id)) {
            recipients.push(crew.userId);
          }
        });
      }
    } else {
      // For shows, collect recipients from assignedUsers subcollection
      try {
        const db = getFirestoreInstance();
        const assignedUsersSnapshot = await getDocs(collection(db, 'shows', eventId, 'assignedUsers'));
        
        assignedUsersSnapshot.forEach(doc => {
          const userData = doc.data();
          if (userData.userId) {
            recipients.push(userData.userId);
          }
        });
      } catch (error) {
        console.error('Error fetching show assignedUsers:', error);
      }
    }
    
    const uniqueRecipients = [...new Set(recipients)];
    const filteredRecipients = uniqueRecipients.filter(id => id !== actorData.userId);
    
    // Determine notification type based on event type
    const notificationType = eventType === 'show'
      ? NOTIFICATION_TYPES.SHOW_RESCHEDULED
      : NOTIFICATION_TYPES.TOUR_DATE_RESCHEDULED;
    
    // Create title and message based on event type
    const title = eventType === 'show' ? 'Show Rescheduled' : 'Tour Date Rescheduled';
    const message = eventType === 'show'
      ? `The show "${event.showName || event.tourName}" has been rescheduled from ${oldDate} to ${newDate}`
      : `A date for ${event.tourName} has been rescheduled from ${oldDate} to ${newDate}`;
    
    for (const userId of filteredRecipients) {
      await createNotification(userId, 'general', {
        type: notificationType,
        title,
        message,
        data: {
          eventId: eventId,
          eventType: eventType,
          eventDateId: eventDate.id,
          actorId: actorData.userId,
          _displayData: {
            tourName: event.tourName,
            showName: eventType === 'show' ? event.showName || event.tourName : null,
            oldDate,
            newDate,
            actorName: actorData.fullName,
            actorPhoto: actorData.profilePictureUrl
          }
        }
      });
      
      // Send push notification if available
      const db = getFirestoreInstance();
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const expoPushToken = userData.expoPushToken;
        
        if (expoPushToken) {
          await sendExpoPushNotification(
            expoPushToken,
            title,
            message,
            {
              type: notificationType,
              eventId: eventId,
              eventType: eventType,
              eventDateId: eventDate.id
            }
          );
        }
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error sending event date rescheduled notification:', error);
    return false;
  }
};

/**
 * Function to subscribe to notifications (legacy name, uses setupNotificationsListener)
 * @param {string} userId - The user ID to subscribe to notifications for
 * @param {Function} callback - Callback function when notifications are received
 * @returns {Function} Unsubscribe function
 */
export const subscribeToNotifications = (userId, callback) => {
  if (!userId) return () => {};
  
  let bandUnsubscribes = [];
  let notificationUnsubscribes = null;

  notificationUnsubscribes = setupNotificationsListener(userId, (data) => {
    const hasUnread = 
      (data.type === 'general' && data.newNotifications?.length > 0) ||
      (data.type === 'invitations' && data.newInvitations?.length > 0);
    
    callback(hasUnread);
  });

  const db = getFirestoreInstance();
  getDoc(doc(db, 'users', userId)).then(userDoc => {
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const bands = userData.associatedActs?.filter(act => act.type === 'band') || [];
      
      bands.forEach(band => {
        const unsubscribe = setupBandNotificationsListener(band.id, userId, (data) => {
          if (data.newNotifications?.length > 0) {
            callback(true);
          }
        });
        bandUnsubscribes.push(unsubscribe);
      });
    }
  }).catch(error => {
    console.error('Error fetching user bands for notifications:', error);
  });

  return () => {
    if (notificationUnsubscribes) notificationUnsubscribes();
    bandUnsubscribes.forEach(unsubscribe => unsubscribe());
  };
};

/**
 * Process notification UI updates based on notification type
 * Works with both tours and shows
 * @param {object} notification - The notification object
 * @param {object} userData - Current user data
 * @param {object} updateFunctions - Functions to update UI state
 */
export const processNotificationUIUpdates = async (notification, userData, updateFunctions) => {
  if (!notification || !userData || !updateFunctions) return;
  
  const { type, data = {} } = notification;
  const { updateBandMembership, refreshBandData, setUserData } = updateFunctions;
  
  const bandId = data.bandId || (notification.bandInfo ? notification.bandInfo.id : null);
  const eventId = data.eventId || data.tourId;
  
  if (!bandId && !eventId) {
    console.log('No bandId or eventId found in notification:', notification);
    return;
  }
  
  try {
    switch (type) {
      case NOTIFICATION_TYPES.USER_ADDED:
      case NOTIFICATION_TYPES.REQUEST_ACCEPTED:
        if (bandId) {
          await updateBandMembership(bandId, 'add');
        }
        break;
        
      case NOTIFICATION_TYPES.USER_REMOVED:
        if (bandId) {
          await updateBandMembership(bandId, 'remove');
        }
        break;
        
      case NOTIFICATION_TYPES.BAND_UPDATED:
        await refreshBandData();
        break;
      
      case NOTIFICATION_TYPES.BAND_DELETED:
        if (bandId) {
          await updateBandMembership(bandId, 'remove');
        }
        break;
      
      case NOTIFICATION_TYPES.ADMIN_ADDED:
        if (bandId) {
          const userIsTarget = data.targetUserId === userData.userId || 
                               notification.data?.targetUserId === userData.userId;
          
          if (userIsTarget) {
            await updateBandMembership(bandId, 'updateRole', 'admin');
          } else {
            await refreshBandData();
          }
        }
        break;
        
      case NOTIFICATION_TYPES.ADMIN_REMOVED:
        if (bandId) {
          const userIsTarget = data.targetUserId === userData.userId || 
                               notification.data?.targetUserId === userData.userId;
          
          if (userIsTarget) {
            await updateBandMembership(bandId, 'updateRole', 'member');
          } else {
            await refreshBandData();
          }
        }
        break;

        case NOTIFICATION_TYPES.VENDOR_STAFF_ACCEPTED:
case NOTIFICATION_TYPES.PAST_VENDOR_STAFF_ACCEPTED:
  // If a vendor's staff request was accepted, update UI if needed
  if (setUserData && userData) {
    const db = getFirestoreInstance();
    const userDoc = await getDoc(doc(db, 'users', userData.userId));
    if (userDoc.exists()) {
      setUserData({
        ...userData,
        ...userDoc.data()
      });
    }
  }
  break;
  
case NOTIFICATION_TYPES.VENDOR_STAFF_PERMISSIONS_UPDATED:
  // If a staff member's permissions were updated
  if (setUserData && userData) {
    const db = getFirestoreInstance();
    const userDoc = await getDoc(doc(db, 'users', userData.userId));
    if (userDoc.exists()) {
      setUserData({
        ...userData,
        ...userDoc.data()
      });
    }
  }
  break;
  
case NOTIFICATION_TYPES.VENDOR_STAFF_MOVED_TO_PAST:
  // If a staff relationship was moved to past
  if (setUserData && userData) {
    const db = getFirestoreInstance();
    const userDoc = await getDoc(doc(db, 'users', userData.userId));
    if (userDoc.exists()) {
      setUserData({
        ...userData,
        ...userDoc.data()
      });
    }
  }
  break;
        
      case NOTIFICATION_TYPES.TOUR_INVITATION:
      case NOTIFICATION_TYPES.SHOW_INVITATION:
      case NOTIFICATION_TYPES.TOUR_DATE_REMOVAL:
      case NOTIFICATION_TYPES.SHOW_REMOVAL:
      case NOTIFICATION_TYPES.TOUR_DATE_RESCHEDULED:
      case NOTIFICATION_TYPES.SHOW_RESCHEDULED:
      case NOTIFICATION_TYPES.TOUR_DATE_CANCELLED:
      case NOTIFICATION_TYPES.SHOW_CANCELLED:
      case NOTIFICATION_TYPES.TOUR_DATE_UNCANCELLED:
      case NOTIFICATION_TYPES.SHOW_UNCANCELLED:
      case NOTIFICATION_TYPES.TASK_ASSIGNED:
      case NOTIFICATION_TYPES.ORGMANAGER_REQUEST:
      case NOTIFICATION_TYPES.ORGARTIST_REQUEST:
      case NOTIFICATION_TYPES.ORGMANAGER_ACCEPTED:
      case NOTIFICATION_TYPES.ORGMANAGER_REJECTED:
      case NOTIFICATION_TYPES.ORGARTIST_ACCEPTED:
      case NOTIFICATION_TYPES.ORGARTIST_REJECTED:
      case NOTIFICATION_TYPES.ORGMANAGER_REMOVAL:
      case NOTIFICATION_TYPES.ORGARTIST_REMOVAL:
      // Financial notification types
      case NOTIFICATION_TYPES.EARNING_RECORDED:
      case NOTIFICATION_TYPES.EXPENSE_OWED:
      case NOTIFICATION_TYPES.EXPENSE_SETTLED:

            // Organization notification handlers
            case NOTIFICATION_TYPES.STAFF_ACCEPTED:
              case NOTIFICATION_TYPES.STAFF_REJECTED:
              case NOTIFICATION_TYPES.STAFF_REMOVAL:
              case NOTIFICATION_TYPES.ORGANIZATION_STAFF_REMOVAL:
                // Refresh user data to update staff lists
                if (setUserData && userData) {
                  const db = getFirestoreInstance();
                  const userDoc = await getDoc(doc(db, 'users', userData.userId));
                  if (userDoc.exists()) {
                    setUserData({
                      ...userData,
                      ...userDoc.data()
                    });
                  }
                }
                break;
                
              // Manager-Artist notification handlers
              case NOTIFICATION_TYPES.ARTIST_MANAGER_REMOVAL:
              case NOTIFICATION_TYPES.MANAGER_ARTIST_REMOVAL:
                // Refresh user data to update managers/ManagingActs lists
                if (setUserData && userData) {
                  const db = getFirestoreInstance();
                  const userDoc = await getDoc(doc(db, 'users', userData.userId));
                  if (userDoc.exists()) {
                    setUserData({
                      ...userData,
                      ...userDoc.data()
                    });
                  }
                }
                break;
                
              // Association notification handlers
              case NOTIFICATION_TYPES.ASSOCIATION_ACCEPTED:
              case NOTIFICATION_TYPES.ASSOCIATION_REJECTED:
              case NOTIFICATION_TYPES.ASSOCIATION_REMOVED:
              case NOTIFICATION_TYPES.BAND_ASSOCIATION_ACCEPTED:
              case NOTIFICATION_TYPES.BAND_ASSOCIATION_REJECTED:
                // Refresh user data to update associated acts
                if (setUserData && userData) {
                  const db = getFirestoreInstance();
                  const userDoc = await getDoc(doc(db, 'users', userData.userId));
                  if (userDoc.exists()) {
                    setUserData({
                      ...userData,
                      ...userDoc.data()
                    });
                  }
                }
                break;

                case NOTIFICATION_TYPES.ASSOCIATED_VENDOR_ACCEPTED:
case NOTIFICATION_TYPES.PAST_ASSOCIATED_VENDOR_ACCEPTED:
case NOTIFICATION_TYPES.ASSOCIATED_VENDOR_REJECTED:
case NOTIFICATION_TYPES.PAST_ASSOCIATED_VENDOR_REJECTED:
case NOTIFICATION_TYPES.ASSOCIATED_VENDOR_REMOVED:
case NOTIFICATION_TYPES.PAST_ASSOCIATED_VENDOR_REMOVED:
case NOTIFICATION_TYPES.ASSOCIATED_VENDOR_MOVED_TO_PAST:
  // Refresh user data to update vendor lists
  if (setUserData && userData) {
    const db = getFirestoreInstance();
    const userDoc = await getDoc(doc(db, 'users', userData.userId));
    if (userDoc.exists()) {
      setUserData({
        ...userData,
        ...userDoc.data()
      });
    }
  }
  break;
        
      default:
        if (bandId) {
          await refreshBandData();
        }
        break;
    }
  } catch (error) {
    console.error('Error processing notification UI updates:', error);
  }
};

/**
 * Accepts an event invitation (tour or show)
 * Works with the pendingInvitations subcollection
 * @param {string} invitationId - The ID of the invitation
 * @param {string} userId - The ID of the user accepting the invitation
 * @returns {Promise<boolean>} Success status
 */
export const acceptEventInvitation = async (invitationId, userId) => {
  const db = getFirestoreInstance();
  
  try {
    // First, get the invitation data from the user's eventInvitations collection
    const invitationRef = doc(db, 'users', userId, 'eventInvitations', invitationId);
    const invitationDoc = await getDoc(invitationRef);
    
    if (!invitationDoc.exists()) {
      throw new Error('Invitation not found in user notifications');
    }
    
    const invitation = invitationDoc.data();
    
    // Verify this invitation belongs to the current user
    if (invitation.userId !== userId) {
      throw new Error('This invitation does not belong to the current user');
    }
    
    // Check if the invitation is already processed
    if (invitation.status !== 'pending') {
      throw new Error(`Invitation is already ${invitation.status}`);
    }
  
    // Determine event type and collection name
    const eventType = invitation.eventType || 'tour';
    const collectionName = eventType === 'tour' ? 'tours' : 'shows';
    const eventId = invitation.eventId || invitation.tourId;
    
    // Find the actual invitation in the pendingInvitations subcollection
    const pendingInvitationsQuery = query(
      collection(db, collectionName, eventId, 'pendingInvitations'),
      where('userId', '==', userId)
    );
    
    const pendingInvitationsSnapshot = await getDocs(pendingInvitationsQuery);
    
    if (pendingInvitationsSnapshot.empty) {
      throw new Error('Invitation not found in pendingInvitations subcollection');
    }
    
    // Get the first matching invitation
    const pendingInvitationDoc = pendingInvitationsSnapshot.docs[0];
    const pendingInvitationId = pendingInvitationDoc.id;
    const pendingInvitation = pendingInvitationDoc.data();
    
    // Now run the transaction for updating Firestore
    await runTransaction(db, async (transaction) => {
      // Get the event document
      const eventRef = doc(db, collectionName, eventId);
      const eventDoc = await transaction.get(eventRef);
      
      if (!eventDoc.exists()) {
        throw new Error(`${eventType.charAt(0).toUpperCase() + eventType.slice(1)} not found`);
      }
      
      // Get the assigned user reference in the assignedUsers subcollection
      const assignedUserRef = doc(db, collectionName, eventId, 'assignedUsers', userId);
      
      // Create the user entry with role, permissions, and assignedDates
      const userToAdd = {
        userId: invitation.userId || '',
        userName: invitation.userName || '',
        fullName: invitation.fullName || '',
        email: invitation.email || '',
        profilePictureUrl: invitation.profilePictureUrl || '',
        userType: invitation.userType || 'Artist',
        userSubType: invitation.userSubType || '',
        role: invitation.role?.toLowerCase() || 'artist',
        permissions: invitation.permissions || {
          canEditSchedule: invitation.role === 'Manager' || invitation.role === 'Crew',
          canAddAttachments: true,
          canAssignTasks: invitation.role === 'Manager' || invitation.role === 'Crew',
          canEditSetlist: invitation.role === 'Artist' || invitation.role === 'Manager',
          canManageGuestlist: true,
          canManageHotels: invitation.role === 'Manager' || invitation.role === 'Crew',
          canManageTickets: invitation.role === 'Manager' || invitation.role === 'Crew',
          canAssignUsers: invitation.role === 'Manager'
        },
        assignedDates: invitation.assignedDates.map(date => ({
          ...date,
          status: 'accepted'
        })),
        idVerificationStatus: invitation.idVerificationStatus || 'Unverified',
        addedBy: invitation.createdBy?.userId || null, // Provide null as a fallback
        createdAt: serverTimestamp()
      };
      
      // Add or update the user in the assignedUsers subcollection
      transaction.set(assignedUserRef, userToAdd);
      
      // Mark the invitation as accepted in user's collection
      transaction.update(invitationRef, {
        status: 'accepted',
        respondedAt: serverTimestamp()
      });
      
      // Mark the invitation as accepted in the pendingInvitations subcollection
      const pendingInvitationRef = doc(db, collectionName, eventId, 'pendingInvitations', pendingInvitationId);
      transaction.update(pendingInvitationRef, {
        status: 'accepted',
        respondedAt: serverTimestamp()
      });
    });

    // After transaction completes successfully, update assignedEvents collection
    // Create a single document in assignedEvents with the eventId as the document ID
    const assignedEventRef = doc(db, 'users', userId, 'assignedEvents', eventId);
    
    // Format all assigned dates
    const formattedAssignedDates = invitation.assignedDates.map(dateInfo => ({
      dateDocId: dateInfo.dateDocId,
      date: normalizeDate(dateInfo.date), // Ensure the date is normalized
      status: 'accepted'
    }));
    
    // Get the event name and other event details
    let eventData = null;
    try {
      const eventRef = doc(db, collectionName, eventId);
      const eventDoc = await getDoc(eventRef);
      if (eventDoc.exists()) {
        eventData = eventDoc.data();
      }
    } catch (error) {
      console.error('Error fetching event data:', error);
    }
    
    // Create a single document with all assigned dates
    await setDoc(assignedEventRef, {
      type: eventType,
      tourId: eventType === 'tour' ? eventId : null,
      showId: eventType === 'show' ? eventId : null,
      role: invitation.role.toLowerCase(),
      assignedDates: formattedAssignedDates,
      permissions: invitation.permissions || {},
      status: 'accepted',
      assignedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      // Include some display info if available
      eventName: eventType === 'tour' ? 
        (eventData?.tourName || invitation.tourName) : 
        (eventData?.showName || eventData?.tourName || invitation.showName || invitation.tourName)
    });
    
    // Update user_events collection (formerly user_tours)
    try {
      // First check if the user_events document exists
      const userEventRef = doc(db, 'user_events', userId, 'assignedEvents', eventId);
      const userEventDoc = await getDoc(userEventRef);
      
      if (userEventDoc.exists()) {
        // Update lastUpdated timestamp
        await updateDoc(userEventRef, {
          lastUpdated: serverTimestamp()
        });
      } else {
        // Create the event document if it doesn't exist
        await setDoc(userEventRef, {
          eventId: eventId,
          eventType: eventType,
          tourName: invitation.tourName || eventData?.tourName,
          showName: eventType === 'show' ? (invitation.showName || invitation.tourName || eventData?.showName || eventData?.tourName) : null,
          actName: invitation.actName || eventData?.actName,
          coverPhotoUrl: invitation.tourCoverPhotoUrl || eventData?.tourCoverPhotoUrl || '',
          userRole: invitation.role.toLowerCase(),
          userType: invitation.userType,
          userSubType: invitation.userSubType,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error updating user_events collection:', error);
      // Continue even if this update fails
    }
    
    // For backward compatibility and for both tours and shows, add dates to user_event_dates (formerly user_tour_dates)
    for (const dateInfo of invitation.assignedDates) {
      try {
        const dateRef = doc(db, collectionName, eventId, 'eventDates', dateInfo.dateDocId);
        const dateDoc = await getDoc(dateRef);
        
        if (dateDoc.exists()) {
          const dateData = dateDoc.data();
          
          // Normalize any dates in the dateData
          if (dateData.date) {
            dateData.date = normalizeDate(dateData.date);
          }
          
          const venueData = dateData.event?.venueDetails?.venueData || {};
          
          // Get user's map privacy setting
          let mapPrivacy = "public"; // Default
          try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
              mapPrivacy = userDoc.data().mapPrivacy || "public";
            }
          } catch (err) {
            console.error('Error getting user map privacy:', err);
          }
          
          // Update user_event_dates (formerly user_tour_dates)
          await updateUserEventDate(
            userId,
            eventId,
            dateInfo.dateDocId,
            {
              id: dateInfo.dateDocId,
              ...dateData,
              eventType: eventType,
              date: normalizeDate(dateData.date) // Ensure date is normalized
            },
            {
              userId,
              fullName: invitation.fullName,
              userName: invitation.userName,
              profilePictureUrl: invitation.profilePictureUrl,
              userType: invitation.userType,
              userSubType: invitation.userSubType,
              idVerificationStatus: invitation.idVerificationStatus || 'Unverified',
              mapPrivacy
            },
            venueData,
            'accepted'
          );
          
          // Also create the entry in user_events/assignedEvents/eventDates
          const userEventDateRef = doc(db, 'user_events', userId, 'assignedEvents', eventId, 'eventDates', dateInfo.dateDocId);
          await setDoc(userEventDateRef, {
            date: normalizeDate(dateData.date),
            dateDocId: dateInfo.dateDocId,
            verified: false,
            venueDetails: venueData,
            userRole: invitation.role.toLowerCase(),
            userType: invitation.userType,
            userSubType: invitation.userSubType,
            mapPrivacy,
            status: 'accepted',
            createdAt: serverTimestamp(),
            lastUpdated: serverTimestamp()
          });
        }
      } catch (error) {
        console.error(`Error updating event date ${dateInfo.dateDocId}:`, error);
      }
    }

    return true;
  } catch (error) {
    console.error('Error accepting event invitation:', error);
    throw error;
  }
};

/**
 * Rejects a tour/show invitation
 * Works with both tour and show collections
 * @param {string} invitationId - The ID of the invitation
 * @param {string} userId - The ID of the user rejecting the invitation
 * @returns {Promise<boolean>} Success status
 */
export const rejectEventInvitation = async (invitationId, userId) => {
  const db = getFirestoreInstance();
  
  try {
    // First, get the invitation from the user's eventInvitations collection
    const invitationRef = doc(db, 'users', userId, 'eventInvitations', invitationId);
    const invitationDoc = await getDoc(invitationRef);
    
    if (!invitationDoc.exists()) {
      throw new Error('Invitation not found in user notifications');
    }
    
    const invitation = invitationDoc.data();
    
    // Verify this invitation belongs to the current user
    if (invitation.userId !== userId) {
      throw new Error('This invitation does not belong to the current user');
    }
    
    // Check if the invitation is already processed
    if (invitation.status !== 'pending') {
      throw new Error(`Invitation is already ${invitation.status}`);
    }
    
    // Determine event type and collection name
    const eventType = invitation.eventType || 'tour';
    const collectionName = eventType === 'tour' ? 'tours' : 'shows';
    const eventId = invitation.eventId || invitation.tourId;
    
    // Find the actual invitation in the pendingInvitations subcollection
    const pendingInvitationsQuery = query(
      collection(db, collectionName, eventId, 'pendingInvitations'),
      where('userId', '==', userId)
    );
    
    const pendingInvitationsSnapshot = await getDocs(pendingInvitationsQuery);
    
    if (pendingInvitationsSnapshot.empty) {
      // If not found in subcollection, just update the user's copy
      await updateDoc(invitationRef, {
        status: 'rejected',
        respondedAt: serverTimestamp()
      });
      
      return true;
    }
    
    // Get the first matching invitation
    const pendingInvitationDoc = pendingInvitationsSnapshot.docs[0];
    const pendingInvitationId = pendingInvitationDoc.id;
    
    return await runTransaction(db, async (transaction) => {
      // Mark the invitation as rejected in the user's collection
      transaction.update(invitationRef, {
        status: 'rejected',
        respondedAt: serverTimestamp()
      });
      
      // Mark the invitation as rejected in the pendingInvitations subcollection
      const pendingInvitationRef = doc(db, collectionName, eventId, 'pendingInvitations', pendingInvitationId);
      transaction.update(pendingInvitationRef, {
        status: 'rejected',
        respondedAt: serverTimestamp()
      });
      
      return true;
    });
  } catch (error) {
    console.error('Error rejecting event invitation:', error);
    throw error;
  }
};

/**
 * Send a recommendation request notification to a user
 * @param {string} recipientId - The ID of the user to send the notification to
 * @param {object} senderData - Data about the sender
 * @param {string} currentUserId - The ID of the current user
 * @returns {Promise<boolean>} Success status
 */
export const sendRecommendationRequestNotification = async (recipientId, senderData, currentUserId) => {
  try {
    const notificationData = {
      type: NOTIFICATION_TYPES.RECOMMENDATION_REQUEST,
      title: 'Recommendation Request',
      message: `${senderData.fullName || 'Someone'} wants you to recommend them. Check out their profile!`,
      data: {
        senderUserId: senderData.userId,
        _displayData: {
          senderUserName: senderData.userName,
          senderFullName: senderData.fullName,
          senderProfilePictureUrl: senderData.profilePictureUrl || '',
        }
      }
    };
    
    await createNotification(recipientId, 'general', notificationData, currentUserId);
    
    return true;
  } catch (error) {
    console.error('Error sending recommendation request notification:', error);
    return false;
  }
};

/**
 * Send notification to vendor when staff adds, edits, or deletes inventory
 * @param {string} vendorId - The ID of the vendor
 * @param {object} staffData - Data about the staff member
 * @param {string} action - The action performed (added, edited, deleted, status_change)
 * @param {object} itemData - Data about the inventory item
 * @returns {Promise<boolean>} Success status
 */
export const sendInventoryUpdateNotification = async (vendorId, staffData, action, itemData) => {
  try {
    let title, message;
    
    switch (action) {
      case 'added':
        title = 'Inventory Item Added';
        message = `${staffData.fullName} added a new ${itemData.type} item: ${itemData.name}`;
        break;
      case 'edited':
        title = 'Inventory Item Updated';
        message = `${staffData.fullName} updated ${itemData.type} item: ${itemData.name}`;
        break;
      case 'deleted':
        title = 'Inventory Item Deleted';
        message = `${staffData.fullName} deleted ${itemData.type} item: ${itemData.name}`;
        break;
      case 'status_change':
        title = 'Inventory Status Changed';
        message = `${staffData.fullName} changed status of ${itemData.name} to ${itemData.status}`;
        break;
      default:
        title = 'Inventory Change';
        message = `${staffData.fullName} made changes to your inventory`;
    }
    
    const notificationData = {
      type: 'INVENTORY_UPDATE',
      title,
      message,
      data: {
        staffId: staffData.userId,
        staffName: staffData.fullName,
        staffUserName: staffData.userName,
        staffProfilePictureUrl: staffData.profilePictureUrl,
        action,
        itemId: itemData.id,
        itemName: itemData.name,
        itemType: itemData.type,
        itemStatus: itemData.status || null,
        category: itemData.category
      }
    };
    
    await createNotification(vendorId, 'general', notificationData);
    
    // Also send push notification if enabled
    try {
      const db = getFirestoreInstance();
      const vendorDoc = await getDoc(doc(db, 'users', vendorId));
      
      if (vendorDoc.exists()) {
        const vendorData = vendorDoc.data();
        const expoPushToken = vendorData.expoPushToken;
        
        if (expoPushToken) {
          await sendExpoPushNotification(
            expoPushToken,
            title,
            message,
            {
              type: 'INVENTORY_UPDATE',
              staffId: staffData.userId,
              itemId: itemData.id,
              action
            }
          );
        }
      }
    } catch (error) {
      console.error('Error sending push notification:', error);
    }
    
    return true;
  } catch (error) {
    console.error('Error sending inventory update notification:', error);
    return false;
  }
};

/**
 * Accepts a staff request for an event (tour or show)
 * Works with both tours and shows
 * @param {string} notificationId - The ID of the notification
 * @param {string} userId - The ID of the user accepting the request
 * @returns {Promise<boolean>} Success status
 */
export const acceptStaffRequest = async (notificationId, userId) => {
  const db = getFirestoreInstance();
  
  try {
    return await runTransaction(db, async (transaction) => {
      // Get the specific notification document
      const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
      const notificationDoc = await transaction.get(notificationRef);
      
      if (!notificationDoc.exists()) {
        // Try invitations collection instead
        const invitationRef = doc(db, 'users', userId, 'eventInvitations', notificationId);
        const invitationDoc = await transaction.get(invitationRef);
        
        if (!invitationDoc.exists()) {
          throw new Error('Notification not found');
        }
        
        // Process as invitation
        const notification = invitationDoc.data();
        
        // Mark the invitation as accepted
        transaction.update(invitationRef, {
          status: 'accepted',
          respondedAt: serverTimestamp()
        });

        // After transaction, add to user_events collection
        try {
          // Determine event type
          const eventType = notification.eventType || 'tour';
          const eventId = notification.eventId || notification.tourId;
          
          // Create the entry in user_events
          const userEventRef = doc(db, 'user_events', userId, 'assignedEvents', eventId);
          await setDoc(userEventRef, {
            eventId: eventId,
            eventType: eventType,
            tourName: notification.tourName,
            showName: eventType === 'show' ? notification.showName || notification.tourName : null,
            actName: notification.actName || '',
            coverPhotoUrl: notification.tourCoverPhotoUrl || '',
            userRole: 'Staff',
            userType: 'Staff',
            userSubType: notification.userSubType || '',
            createdAt: serverTimestamp(),
            lastUpdated: serverTimestamp()
          });
          
          // Get user's map privacy setting
          let mapPrivacy = "public"; // Default
          try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
              mapPrivacy = userDoc.data().mapPrivacy || "public";
            }
          } catch (err) {
            console.error('Error getting user map privacy:', err);
          }
          
          // Process each assigned date
          if (notification.assignedDates && notification.assignedDates.length > 0) {
            const collectionName = eventType === 'tour' ? 'tours' : 'shows';
            
            for (const assignedDate of notification.assignedDates) {
              const dateId = assignedDate.dateDocId;
              
              // Get the event date data
              const dateRef = doc(db, collectionName, eventId, 'eventDates', dateId);
              const dateDoc = await getDoc(dateRef);
              
              if (dateDoc.exists()) {
                const dateData = {
                  id: dateId,
                  ...dateDoc.data()
                };
                
                const venueData = dateData.event?.venueDetails?.venueData || {};
                
                // Create entry in user_events/eventDates
                const userEventDateRef = doc(db, 'user_events', userId, 'assignedEvents', eventId, 'eventDates', dateId);
                await setDoc(userEventDateRef, {
                  date: normalizeDate(dateData.date), // Normalize the date
                  dateDocId: dateId,
                  verified: false,
                  venueDetails: venueData,
                  userRole: 'Staff',
                  userType: 'Staff',
                  userSubType: notification.userSubType || '',
                  mapPrivacy,
                  eventType: eventType,
                  createdAt: serverTimestamp(),
                  lastUpdated: serverTimestamp()
                });
                
                // Normalize date in dateData before updating user_event_dates
                if (dateData.date) {
                  dateData.date = normalizeDate(dateData.date);
                }
                
                // Update user_event_dates
                await updateUserEventDate(
                  userId,
                  eventId,
                  dateId,
                  {
                    ...dateData,
                    eventType: eventType,
                    date: normalizeDate(dateData.date) // Ensure date is normalized
                  },
                  {
                    userId,
                    fullName: notification.fullName,
                    userName: notification.userName,
                    profilePictureUrl: notification.profilePictureUrl,
                    userType: 'Staff',
                    userSubType: notification.userSubType,
                    mapPrivacy
                  },
                  venueData,
                  'accepted'
                );
              }
            }
          }
        } catch (syncError) {
          console.error('Error synchronizing user_events for staff:', syncError);
        }
        
        return true;
      }
      
      const notification = notificationDoc.data();
      
      // Get important values from notification
      const organizationId = notification.organizationId;
      const permissions = notification.permissions || {
        canAddItems: false,
        canEditItems: false,
        canDeleteItems: false,
        canChangeStatus: false
      };
      
      // Mark the notification as accepted
      transaction.update(notificationRef, {
        status: 'accepted',
        respondedAt: serverTimestamp()
      });

      // After transaction, add to user_events collection
      try {
        const eventType = notification.eventType || 'tour';
        const eventId = notification.eventId || notification.tourId;
        
        // Add to user_events collection
        if (eventId) {
          const userEventRef = doc(db, 'user_events', userId, 'assignedEvents', eventId);
          await setDoc(userEventRef, {
            eventId: eventId,
            eventType: eventType,
            tourName: notification.tourName || '',
            showName: eventType === 'show' ? notification.showName || notification.tourName : null,
            actName: notification.actName || '',
            coverPhotoUrl: notification.tourCoverPhotoUrl || '',
            userRole: 'Staff',
            userType: 'Staff',
            userSubType: notification.userSubType || '',
            createdAt: serverTimestamp(),
            lastUpdated: serverTimestamp()
          });
        }
        
        // Get user's map privacy setting
        let mapPrivacy = "public"; // Default
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            mapPrivacy = userDoc.data().mapPrivacy || "public";
          }
        } catch (err) {
          console.error('Error getting user map privacy:', err);
        }
        
        // Process each assigned date
        if (eventId && notification.assignedDates && notification.assignedDates.length > 0) {
          const collectionName = eventType === 'tour' ? 'tours' : 'shows';
          
          for (const assignedDate of notification.assignedDates) {
            const dateId = assignedDate.dateDocId;
            
            // Get the event date data
            const dateRef = doc(db, collectionName, eventId, 'eventDates', dateId);
            const dateDoc = await getDoc(dateRef);
            
            if (dateDoc.exists()) {
              const dateData = {
                id: dateId,
                ...dateDoc.data()
              };
              
              const venueData = dateData.event?.venueDetails?.venueData || {};
              
              // Create entry in user_events/eventDates
              const userEventDateRef = doc(db, 'user_events', userId, 'assignedEvents', eventId, 'eventDates', dateId);
              await setDoc(userEventDateRef, {
                date: normalizeDate(dateData.date),
                dateDocId: dateId,
                verified: false,
                venueDetails: venueData,
                userRole: 'Staff',
                userType: 'Staff',
                userSubType: notification.userSubType || '',
                mapPrivacy,
                eventType: eventType,
                createdAt: serverTimestamp(),
                lastUpdated: serverTimestamp()
              });
              
              // Update user_event_dates
              await updateUserEventDate(
                userId,
                eventId,
                dateId,
                {
                  ...dateData,
                  eventType: eventType
                },
                {
                  userId,
                  fullName: notification.fullName,
                  userName: notification.userName,
                  profilePictureUrl: notification.profilePictureUrl,
                  userType: 'Staff',
                  userSubType: notification.userSubType,
                  mapPrivacy
                },
                venueData,
                'accepted'
              );
            }
          }
        }
      } catch (syncError) {
        console.error('Error synchronizing user_events for staff:', syncError);
      }
      
      return true;
    });
  } catch (error) {
    console.error('Error accepting staff request:', error);
    throw error;
  }
};

/**
 * Rejects a staff request for an event (tour or show)
 * Works with both tours and shows
 * @param {string} notificationId - The ID of the notification
 * @param {string} userId - The ID of the user rejecting the request
 * @returns {Promise<boolean>} Success status
 */
export const rejectStaffRequest = async (notificationId, userId) => {
  const db = getFirestoreInstance();
  
  try {
    return await runTransaction(db, async (transaction) => {
      // Get the specific notification document
      const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
      const notificationDoc = await transaction.get(notificationRef);
      
      if (!notificationDoc.exists()) {
        // Try invitations collection instead
        const invitationRef = doc(db, 'users', userId, 'eventInvitations', notificationId);
        const invitationDoc = await transaction.get(invitationRef);
        
        if (!invitationDoc.exists()) {
          throw new Error('Notification not found');
        }
        
        // Process as invitation
        const notification = invitationDoc.data();
        
        // Mark the invitation as rejected
        transaction.update(invitationRef, {
          status: 'rejected',
          respondedAt: serverTimestamp()
        });
        
        // Find and update the corresponding invitation in pendingInvitations subcollection
        if (notification.eventId || notification.tourId) {
          const eventId = notification.eventId || notification.tourId;
          const eventType = notification.eventType || 'tour';
          const collectionName = eventType === 'tour' ? 'tours' : 'shows';
          
          const pendingInvitationsQuery = query(
            collection(db, collectionName, eventId, 'pendingInvitations'),
            where('userId', '==', userId)
          );
          
          const pendingSnapshot = await getDocs(pendingInvitationsQuery);
          if (!pendingSnapshot.empty) {
            const pendingDoc = pendingSnapshot.docs[0];
            transaction.update(pendingDoc.ref, {
              status: 'rejected',
              respondedAt: serverTimestamp()
            });
          }
        }
        
        return true;
      }
      
      const notification = notificationDoc.data();
      
      // Mark the notification as rejected
      transaction.update(notificationRef, {
        status: 'rejected',
        respondedAt: serverTimestamp()
      });
      
      return true;
    });
  } catch (error) {
    console.error('Error rejecting staff request:', error);
    throw error;
  }
};

/**
 * Accepts a vendor staff request
 * @param {string} notificationId - The ID of the notification
 * @param {string} userId - The ID of the user accepting the request
 * @returns {Promise<boolean>} Success status
 */
export const acceptVendorStaffRequest = async (notificationId, userId) => {
  const db = getFirestoreInstance();
  
  try {
    // Get the notification data
    const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
    const notificationDoc = await getDoc(notificationRef);
    
    if (!notificationDoc.exists()) {
      throw new Error('Notification not found');
    }
    
    const notification = notificationDoc.data();
    const vendorId = notification.organizationId;
    const requestId = notification.requestId || notification.data?.staffData?.staffId;
    const isPast = notification.isPast || false;
    
    // Mark notification as accepted
    await updateDoc(notificationRef, {
      status: 'accepted',
      respondedAt: serverTimestamp()
    });
    
    // Get the pending request from the vendor's pendingVendorStaff subcollection
    let pendingRequest = null;
    
    if (requestId) {
      const pendingRequestRef = doc(db, 'users', vendorId, isPast ? 'pendingPastVendorStaff' : 'pendingVendorStaff', requestId);
      const pendingRequestDoc = await getDoc(pendingRequestRef);
      
      if (pendingRequestDoc.exists()) {
        pendingRequest = {
          id: pendingRequestDoc.id,
          ...pendingRequestDoc.data()
        };
      }
    }
    
    // Extract date information from the notification or pending request
    let dateInfo = {};
    
    // IMPORTANT: Handle date information differently based on whether it's past or current
    if (isPast) {
      // For past relationships, we need duration
      const duration = notification.data?._displayData?.duration || 
                       pendingRequest?.duration || 
                       {
                         startMonth: notification.data?._displayData?.startMonth || "1",
                         startYear: notification.data?._displayData?.startYear || "2020",
                         endMonth: notification.data?._displayData?.endMonth || "12",
                         endYear: notification.data?._displayData?.endYear || "2024"
                       };
      
      dateInfo = { duration };
    } else {
      // For current relationships, we need since
      const since = notification.data?._displayData?.since || 
                    pendingRequest?.since || 
                    {
                      month: notification.data?._displayData?.sinceMonth || "1",
                      year: notification.data?._displayData?.sinceYear || "2024"
                    };
      
      dateInfo = { since };
    }
    
    // Create the staff record with the correct date information
    const staffData = {
      userId: userId,
      status: 'accepted',
      permissions: notification.permissions || {},
      isPast: isPast,
      createdAt: pendingRequest?.createdAt || new Date().toISOString(),
      acceptedAt: new Date().toISOString(),
      ...dateInfo  // This will add either since or duration depending on isPast
    };
    
    // Create the staff record in the vendorStaff subcollection
    const vendorStaffRef = collection(db, 'users', vendorId, 'vendorStaff');
    const staffDocRef = await addDoc(vendorStaffRef, staffData);
    
    // Store the ID of the new record to link both sides
    const staffDocId = staffDocRef.id;
    
    // Create entry in staff's associatedVendors subcollection
    const staffAssociatedVendorData = {
      vendorId: vendorId,
      status: 'active',
      permissions: notification.permissions || {},
      isPast: isPast,
      createdAt: pendingRequest?.createdAt || new Date().toISOString(),
      acceptedAt: new Date().toISOString(),
      otherSideDocId: staffDocId, // Link to the vendor's document
      ...dateInfo  // Add the same date info here
    };
    
    // Add to staff's associatedVendors subcollection
    const associatedVendorsRef = collection(db, 'users', userId, 'associatedVendors');
    const associatedVendorDocRef = await addDoc(associatedVendorsRef, staffAssociatedVendorData);
    
    // Update the vendor's staff document with the reference to the staff's document
    await updateDoc(doc(db, 'users', vendorId, 'vendorStaff', staffDocId), {
      otherSideDocId: associatedVendorDocRef.id
    });
    
     // Get vendor data for updating arrays in main documents
     const vendorDoc = await getDoc(doc(db, 'users', vendorId));
     let vendorData = {};
     
     if (vendorDoc.exists()) {
       vendorData = vendorDoc.data();
     } else {
       throw new Error('Vendor not found');
     }
     
     // Update vendor's main document with top 3 staff
     // Get the top 3 staff from the subcollection
     const top3Query = query(
       vendorStaffRef,
       where('isPast', '==', isPast),
       orderBy('createdAt', 'desc'),
       limit(3)
     );
     
     const top3Snapshot = await getDocs(top3Query);
     const top3Staff = await Promise.all(
       top3Snapshot.docs.map(async (docSnapshot) => {
         const staffData = docSnapshot.data();
         
         // Enrich with user data
         if (staffData.userId) {
           try {
             const userDoc = await getDoc(doc(db, 'users', staffData.userId));
             if (userDoc.exists()) {
               const userData = userDoc.data();
               return {
                 id: docSnapshot.id,
                 ...staffData,
                 fullName: userData.fullName || '',
                 userName: userData.userName || '',
                 profilePictureUrl: userData.profilePictureUrl || '',
                 userSubType: userData.userSubType || '',
                 idVerificationStatus: userData.idVerificationStatus || ''
               };
             }
           } catch (error) {
             console.error(`Error fetching user data for ${staffData.userId}:`, error);
           }
         }
         
         return {
           id: docSnapshot.id,
           ...staffData
         };
       })
     );
     
     // Update the vendor's main document
     await updateDoc(doc(db, 'users', vendorId), {
       [isPast ? 'pastVendorStaff' : 'vendorStaff']: top3Staff,
       [isPast ? 'pastVendorStaffCount' : 'vendorStaffCount']: increment(1)
     });
     
     // Update staff's main document with top 3 vendors
     // Get the top 3 vendors from the subcollection
     const top3VendorsQuery = query(
       associatedVendorsRef,
       where('isPast', '==', isPast),
       orderBy('createdAt', 'desc'),
       limit(3)
     );
     
     const top3VendorsSnapshot = await getDocs(top3VendorsQuery);
     const top3Vendors = await Promise.all(
       top3VendorsSnapshot.docs.map(async (docSnapshot) => {
         const vendorData = docSnapshot.data();
         
         // Enrich with vendor data
         if (vendorData.vendorId) {
           try {
             const vendorDoc = await getDoc(doc(db, 'users', vendorData.vendorId));
             if (vendorDoc.exists()) {
               const vendorUserData = vendorDoc.data();
               return {
                 id: docSnapshot.id,
                 ...vendorData,
                 fullName: vendorUserData.fullName || '',
                 userName: vendorUserData.userName || '',
                 profilePictureUrl: vendorUserData.profilePictureUrl || '',
                 userSubType: vendorUserData.userSubType || ''
               };
             }
           } catch (error) {
             console.error(`Error fetching vendor data for ${vendorData.vendorId}:`, error);
           }
         }
         
         return {
           id: docSnapshot.id,
           ...vendorData
         };
       })
     );
     
     // Update staff's main document
     await updateDoc(doc(db, 'users', userId), {
       [isPast ? 'pastAssociatedVendors' : 'associatedVendors']: top3Vendors,
       [isPast ? 'pastAssociatedVendorsCount' : 'associatedVendorsCount']: increment(1)
     });
     
     // Remove the pending request
     if (requestId) {
       await deleteDoc(doc(db, 'users', vendorId, isPast ? 'pendingPastVendorStaff' : 'pendingVendorStaff', requestId));
     }
     
     // Send acceptance notification to vendor
     await createNotification(vendorId, 'general', {
       type: isPast ? NOTIFICATION_TYPES.PAST_VENDOR_STAFF_ACCEPTED : NOTIFICATION_TYPES.VENDOR_STAFF_ACCEPTED,
       title: `${isPast ? 'Past ' : ''}Staff Request Accepted`,
       message: `${notification.fullName} has accepted your ${isPast ? 'past ' : ''}staff request.`,
       userId: userId,
       status: 'accepted',
       timestamp: new Date().toISOString(),
       data: {
         _displayData: {
           userName: notification.userName,
           fullName: notification.fullName,
           profilePictureUrl: notification.profilePictureUrl || ''
         }
       }
     });
     
     return true;
   } catch (error) {
     console.error('Error accepting vendor staff request:', error);
     throw error;
   }
 };

/**
 * Rejects a vendor staff request
 * @param {string} notificationId - The ID of the notification
 * @param {string} userId - The ID of the user rejecting the request
 * @returns {Promise<boolean>} Success status
 */
export const rejectVendorStaffRequest = async (notificationId, userId) => {
  const db = getFirestoreInstance();
  
  try {
    // Get the notification data
    const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
    const notificationDoc = await getDoc(notificationRef);
    
    if (!notificationDoc.exists()) {
      throw new Error('Notification not found');
    }
    
    const notification = notificationDoc.data();
    const vendorId = notification.organizationId;
    const requestId = notification.requestId || notification.data?.staffData?.staffId;
    const isPast = notification.isPast || false;
    
    // Mark notification as rejected
    await updateDoc(notificationRef, {
      status: 'rejected',
      respondedAt: serverTimestamp()
    });
    
    // Remove the pending request
    if (requestId) {
      await deleteDoc(doc(db, 'users', vendorId, isPast ? 'pendingPastVendorStaff' : 'pendingVendorStaff', requestId));
    }
    
    // Send rejection notification to vendor
    await createNotification(vendorId, 'general', {
      type: isPast ? NOTIFICATION_TYPES.PAST_VENDOR_STAFF_REJECTED : NOTIFICATION_TYPES.VENDOR_STAFF_REJECTED,
      title: `${isPast ? 'Past ' : ''}Staff Request Declined`,
      message: `${notification.fullName} has declined your ${isPast ? 'past ' : ''}staff request.`,
      userId: userId,
      status: 'rejected',
      timestamp: new Date().toISOString(),
      data: {
        _displayData: {
          userName: notification.userName,
          fullName: notification.fullName,
          profilePictureUrl: notification.profilePictureUrl || ''
        }
      }
    });
    
    return true;
  } catch (error) {
    console.error('Error rejecting vendor staff request:', error);
    throw error;
  }
};

/**
 * Send a tour/show invitation to a band (creates a notification for band admins)
 * Works with both tours and shows
 * @param {string} bandId - The ID of the band
 * @param {object} invitationData - The invitation data
 * @returns {Promise<boolean>} Success status
 */
export const sendBandEventInvitationNotification = async (bandId, invitationData) => {
  try {
    // Determine event type
    const eventType = invitationData.eventType || 'tour';
    
    // Create band notification
    const bandNotificationData = {
      bandId,
      type: eventType === 'show' ? 'SHOW_INVITATION' : 'TOUR_INVITATION',
      title: `New ${eventType === 'show' ? 'Show' : 'Tour'} Invitation`,
      message: `Your band has been invited to join ${eventType === 'show' ? 'the show' : 'the tour'} "${invitationData.tourName || invitationData.showName}"`,
      recipientIds: invitationData.recipientIds || [],
      actionBy: invitationData.createdBy,
      data: {
        ...invitationData,
        eventId: invitationData.eventId || invitationData.tourId,
        eventType: eventType,
        _displayData: {
          tourName: invitationData.tourName,
          showName: eventType === 'show' ? invitationData.showName || invitationData.tourName : null,
          actName: invitationData.actName,
          creatorName: invitationData.createdBy?.fullName,
          creatorPhoto: invitationData.createdBy?.profilePictureUrl
        }
      }
    };
    
    await createBandNotification(bandNotificationData);
    
    return true;
  } catch (error) {
    console.error(`Error sending ${invitationData.eventType || 'tour'} invitation notification to band:`, error);
    return false;
  }
};

/**
 * Accept a tour/show invitation for a band
 * Works with both tours and shows
 * @param {object} notification - The notification object
 * @param {object} userData - The user data of the person accepting the invitation
 * @returns {Promise<boolean>} Success status
 */
export const acceptBandEventInvitation = async (notification, userData) => {
  const db = getFirestoreInstance();
  
  try {
    // Get band and notification data
    const bandId = notification.bandInfo?.id;
    const eventId = notification.data?.eventId || notification.data?.tourId;
    const eventType = notification.data?.eventType || 'tour';
    const collectionName = eventType === 'tour' ? 'tours' : 'shows';
    
    if (!bandId || !eventId) {
      throw new Error('Missing band or event information');
    }
    
    // Get the event document
    const eventRef = doc(db, collectionName, eventId);
    const eventDoc = await getDoc(eventRef);
    
    if (!eventDoc.exists()) {
      throw new Error(`${eventType.charAt(0).toUpperCase() + eventType.slice(1)} not found`);
    }
    
    const eventData = eventDoc.data();
    
    // Add band to assignedUsers subcollection
    const assignedUserRef = doc(db, collectionName, eventId, 'assignedUsers', `band_${bandId}`);
    
    // Create the band entry with permissions
    const bandToAdd = {
      id: bandId,
      role: 'band',
      bandDetails: {
        bandFullName: notification.data?.bandName,
        bandUserName: notification.data?.bandUserName,
        bandProfilePictureUrl: notification.data?.bandProfilePictureUrl || '',
        bandGenres: notification.data?.bandGenres || []
      },
      assignedDates: notification.data?.assignedDates || [],
      permissions: {
        canEditSchedule: false,
        canAddAttachments: true,
        canAssignTasks: false,
        canEditSetlist: true,
        canManageGuestlist: true,
        canManageHotels: false,
        canManageTickets: false,
        canAssignUsers: false
      },
      addedBy: notification.actionBy?.userId,
      createdAt: serverTimestamp()
    };
    
    await setDoc(assignedUserRef, bandToAdd);
    
    // Add to band_events collection for backwards compatibility
    const bandEventRef = doc(db, 'band_events', bandId, 'assignedEvents', eventId);
    await setDoc(bandEventRef, {
      eventId,
      eventType: eventType,
      tourName: eventData.tourName,
      showName: eventType === 'show' ? eventData.showName || eventData.tourName : null,
      actName: eventData.actName,
      actType: eventData.actType,
      coverPhotoUrl: eventData.tourCoverPhotoUrl || '',
      createdAt: serverTimestamp(),
      lastUpdated: serverTimestamp()
    });
    
    // Add assigned dates to band_events
    if (notification.data?.assignedDates && notification.data.assignedDates.length > 0) {
      for (const assignedDate of notification.data.assignedDates) {
        const dateId = assignedDate.dateDocId;
        
        try {
          // Get the date data
          const dateRef = doc(db, collectionName, eventId, 'eventDates', dateId);
          const dateDoc = await getDoc(dateRef);
          
          if (dateDoc.exists()) {
            const dateData = dateDoc.data();
            const venueData = dateData.event?.venueDetails?.venueData || {};
            
            // Create entry in band_events/events/eventDates for backwards compatibility
            const bandEventDateRef = doc(db, 'band_events', bandId, 'assignedEvents', eventId, 'eventDates', dateId);
            await setDoc(bandEventDateRef, {
              date: normalizeDate(dateData.date), // Normalize the date
              dateDocId: dateId,
              verified: false,
              venueDetails: venueData,
              eventType: eventType,
              createdAt: serverTimestamp(),
              lastUpdated: serverTimestamp()
            });
          }
        } catch (error) {
          console.error(`Error adding band event date ${dateId}:`, error);
        }
      }
    }
    
    // Update the band document to add assignedEvents field
    const bandRef = doc(db, 'bands', bandId);
    const bandDoc = await getDoc(bandRef);
    
    if (bandDoc.exists()) {
      const bandData = bandDoc.data();
      const existingAssignedEvents = bandData.assignedEvents || [];
      
      // Check if this event is already assigned
      if (!existingAssignedEvents.some(event => event.eventId === eventId)) {
        const newEventAssignment = {
          eventId,
          eventType: eventType,
          tourName: eventData.tourName,
          showName: eventType === 'show' ? eventData.showName || eventData.tourName : null,
          actName: eventData.actName,
          assignedAt: new Date()
        };
        
        await updateDoc(bandRef, {
          assignedEvents: [...existingAssignedEvents, newEventAssignment]
        });
      }
    }
    
    // Mark notification as accepted by updating readBy
    await markBandNotificationAsRead(bandId, notification.id, userData.userId);
    
    return true;
  } catch (error) {
    console.error('Error accepting band event invitation:', error);
    throw error;
  }
};

/**
 * Reject a tour/show invitation for a band
 * Works with both tours and shows
 * @param {object} notification - The notification object
 * @param {object} userData - The user data of the person rejecting the invitation
 * @returns {Promise<boolean>} Success status
 */
export const rejectBandEventInvitation = async (notification, userData) => {
  const db = getFirestoreInstance();
  
  try {
    const bandId = notification.bandInfo?.id;
    
    if (!bandId) {
      throw new Error('Missing band information');
    }
    
    // Mark notification as rejected
    await markBandNotificationAsRead(bandId, notification.id, userData.userId);
    
    // Add a flag indicating the notification was rejected
    const eventId = notification.data?.eventId || notification.data?.tourId;
    const eventType = notification.data?.eventType || 'tour';
    const collectionName = eventType === 'tour' ? 'tours' : 'shows';
    
    // Find the invitation in the pendingInvitations subcollection
    if (eventId) {
      try {
        const pendingInvitationsQuery = query(
          collection(db, collectionName, eventId, 'pendingInvitations'),
          where('bandId', '==', bandId)
        );
        
        const pendingInvitationsSnapshot = await getDocs(pendingInvitationsQuery);
        
        if (!pendingInvitationsSnapshot.empty) {
          const pendingInvitationDoc = pendingInvitationsSnapshot.docs[0];
          
          // Update the invitation status
          await updateDoc(pendingInvitationDoc.ref, {
            status: 'rejected',
            respondedAt: serverTimestamp()
          });
        }
      } catch (error) {
        console.error('Error updating pending invitation status:', error);
        // Continue even if this fails
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error rejecting band event invitation:', error);
    throw error;
  }
};

/**
 * Function to verify a band's event date
 * Works with both tours and shows
 * @param {string} bandId - The ID of the band
 * @param {string} eventId - The ID of the event (tour or show)
 * @param {string} eventDateId - The ID of the event date
 * @param {string} eventType - The type of event ('tour' or 'show')
 * @returns {Promise<boolean>} Success status
 */
export const verifyBandEventDate = async (bandId, eventId, eventDateId, eventType = 'tour') => {
  const db = getFirestoreInstance();
  
  try {
    // Update the date in band_events to verified
    const bandEventDateRef = doc(db, 'band_events', bandId, 'assignedEvents', eventId, 'eventDates', eventDateId);
    await updateDoc(bandEventDateRef, {
      verified: true,
      verifiedAt: serverTimestamp()
    });
    
    // Check if the band's event count needs to be incremented
    const bandRef = doc(db, 'bands', bandId);
    const bandDoc = await getDoc(bandRef);
    
    if (bandDoc.exists()) {
      const bandData = bandDoc.data();
      
      // Check if any dates in this event are already verified
      const bandEventDatesQuery = query(
        collection(db, 'band_events', bandId, 'assignedEvents', eventId, 'eventDates'),
        where('verified', '==', true)
      );
      
      const bandEventDatesSnapshot = await getDocs(bandEventDatesQuery);
      
      // If this is the first verified date for this event, increment eventsCount
      if (bandEventDatesSnapshot.size === 1) {
        // Only increment if this is the first verified date
        if (eventType === 'tour') {
          await updateDoc(bandRef, {
            toursCount: (bandData.toursCount || 0) + 1
          });
        } else {
          await updateDoc(bandRef, {
            showsCount: (bandData.showsCount || 0) + 1
          });
        }
      }
      
      // Always increment showsCount (date count) for both tours and shows
      await updateDoc(bandRef, {
        showsCount: (bandData.showsCount || 0) + 1
      });
    }
    
    return true;
  } catch (error) {
    console.error(`Error verifying band ${eventType} date:`, error);
    throw error;
  }
};

/**
 * Update user's event date information in user_event_dates collection
 * Replaces updateUserTourDate function
 * @param {string} userId - The user ID
 * @param {string} eventId - The event ID (tour or show)
 * @param {string} eventDateId - The event date ID
 * @param {object} eventDateData - Data about the event date
 * @param {object} userData - User data to include with the date
 * @param {object} venueData - Venue details to include
 * @param {string} status - Status of the date (accepted, rejected, etc.)
 * @returns {Promise<object>} The updated date document
 */
export const updateUserEventDate = async (userId, eventId, eventDateId, eventDateData, userData, venueData, status = 'pending') => {
  const db = getFirestoreInstance();
  
  try {
    // Create a document in user_event_dates collection with combined ID
    const docId = `${userId}_${eventId}_${eventDateId}`;
    const userEventDateRef = doc(db, 'user_event_dates', docId);
    
    // Prepare the document data with user details at the top level (not nested)
    const userEventDateData = {
      userId,
      eventId,
      eventDateId,
      eventType: eventDateData.eventType || 'tour',
      date: normalizeDate(eventDateData.date),
      eventName: eventDateData.tourName || eventDateData.showName || '',
      actName: eventDateData.actName || '',
      status,
      
      // User details at top level instead of nested in userData
      fullName: userData.fullName || '',
      userName: userData.userName || '',
      profilePictureUrl: userData.profilePictureUrl || '',
      userType: userData.userType || '',
      userSubType: userData.userSubType || '',
      idVerificationStatus: userData.idVerificationStatus || 'Unverified',
      mapPrivacy: userData.mapPrivacy || 'public',
      
      // Venue details nested in a venue map
      venue: {
        displayName: venueData?.displayName || '',
        name: venueData?.name || '',
        city: venueData?.city || '',
        state: venueData?.state || '',
        country: venueData?.country || '',
        formattedAddress: venueData?.formattedAddress || '',
        shortFormattedAddress: venueData?.shortFormattedAddress || '',
        latitude: venueData?.latitude || null,
        longitude: venueData?.longitude || null,
        location: venueData?.location || { latitude: null, longitude: null },
        id: venueData?.id || '',
        googleMapsUri: venueData?.googleMapsUri || '',
        placeCoverPhotoUrl: venueData?.placeCoverPhotoUrl || '',
        accessibilityOptions: venueData?.accessibilityOptions || {},
        addressComponents: venueData?.addressComponents || '',
        adrFormatAddress: venueData?.adrFormatAddress || '',
        businessStatus: venueData?.businessStatus || '',
        currentOpeningHours: venueData?.currentOpeningHours || '',
        currentSecondaryOpeningHours: venueData?.currentSecondaryOpeningHours || '',
        iconBackgroundColor: venueData?.iconBackgroundColor || '',
        iconMaskBaseUri: venueData?.iconMaskBaseUri || '',
        internationalPhoneNumber: venueData?.internationalPhoneNumber || '',
        nationalPhoneNumber: venueData?.nationalPhoneNumber || '',
        pincode: venueData?.pincode || '',
        plusCode: venueData?.plusCode || '',
        priceLevel: venueData?.priceLevel || '',
        primaryType: venueData?.primaryType || '',
        primaryTypeDisplayName: venueData?.primaryTypeDisplayName || '',
        rating: venueData?.rating || '',
        regularOpeningHours: venueData?.regularOpeningHours || '',
        regularSecondaryOpeningHours: venueData?.regularSecondaryOpeningHours || '',
        subDestinations: venueData?.subDestinations || '',
        types: venueData?.types || [],
        userRatingCount: venueData?.userRatingCount || '',
        utcOffsetMinutes: venueData?.utcOffsetMinutes || '',
        viewport: venueData?.viewport || null,
        websiteUri: venueData?.websiteUri || ''
      },
      
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp()
    };
    
    // Set the document
    await setDoc(userEventDateRef, userEventDateData);
    
    return userEventDateData;
  } catch (error) {
    console.error('Error updating user event date:', error);
    throw error;
  }
};

/**
 * Function to create notification for organization management
 * Updated to work with ManagingActs subcollection
 * Removed role references
 */
export const createOrganizationNotification = async (userId, organizationId, actData, isPast = false) => {
  try {
    const db = getFirestoreInstance();
    
    // Get organization data
    const orgDoc = await getDoc(doc(db, 'users', organizationId));
    if (!orgDoc.exists()) {
      throw new Error('Organization not found');
    }
    
    const orgData = orgDoc.data();
    
    // Create the notification data - store only IDs, not full profile data
    const notificationData = {
      type: actData.type === 'artist' ? 
        (isPast ? NOTIFICATION_TYPES.PAST_ORGARTIST_REQUEST : NOTIFICATION_TYPES.ORGARTIST_REQUEST) :
        (isPast ? NOTIFICATION_TYPES.PAST_ORGBAND_REQUEST : NOTIFICATION_TYPES.ORGBAND_REQUEST),
      title: actData.type === 'artist' ? 
        (isPast ? 'Past Organization Artist Request' : 'Organization Artist Request') :
        (isPast ? 'Past Organization Band Request' : 'Organization Band Request'),
      message: `${orgData.fullName || 'An organization'} wants to add you as their ${isPast ? 'past ' : ''}${actData.type === 'artist' ? 'managed artist' : 'managed band'}.`,
      organizationId: organizationId,
      userId: userId,
      requestId: actData.id,
      isPast: isPast,
      status: 'pending',
      timestamp: new Date().toISOString(),
      data: {
        actData: {
          ...actData,
          actId: actData.id
        },
        _displayData: {
          organizationName: orgData.fullName,
          isPast: isPast
        }
      }
    };
    
    // Create the notification
    await createNotification(userId, 'general', notificationData);
    
    return true;
  } catch (error) {
    console.error('Error creating organization notification:', error);
    return false;
  }
};

/**
 * Accepts a manager request for an organization
 * @param {string} notificationId - The ID of the notification
 * @param {string} userId - The ID of the user accepting the request
 * @returns {Promise<boolean>} Success status
 */
export const acceptManagerRequest = async (notificationId, userId) => {
  const db = getFirestoreInstance();
  
  try {
    // Get the notification data outside the transaction
    const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
    const notificationDoc = await getDoc(notificationRef);
    
    if (!notificationDoc.exists()) {
      throw new Error('Notification not found');
    }
    
    const notification = notificationDoc.data();
    const organizationId = notification.organizationId;
    
    // Now run the transaction with proper reads-before-writes
    await runTransaction(db, async (transaction) => {
      // Do ALL reads first
      const managerRef = doc(db, 'users', organizationId, 'managedManagers', userId);
      const managerDoc = await transaction.get(managerRef);
      
      // Now do ALL writes
      // Update notification status
      transaction.update(notificationRef, {
        status: 'accepted',
        respondedAt: serverTimestamp()
      });
      
      // Update or create manager record in managedManagers subcollection - use minimal data
      if (managerDoc.exists()) {
        transaction.update(managerRef, {
          status: 'accepted',
          respondedAt: serverTimestamp()
        });
      } else {
        transaction.set(managerRef, {
          userId: userId,
          status: 'accepted',
          role: 'manager',
          addedAt: notification.timestamp || serverTimestamp(),
          respondedAt: serverTimestamp()
        });
      }
      
      // Add organization to manager's organizations subcollection with minimal data
      const orgRef = doc(db, 'users', userId, 'organizations', organizationId);
      transaction.set(orgRef, {
        organizationId: organizationId,
        role: 'manager',
        status: 'accepted',
        joinedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: notification.createdBy?.userId || null
      });
    });
    
    // Create notification for the organization owner after transaction completes
    await createNotification(organizationId, 'general', {
      type: NOTIFICATION_TYPES.ORGMANAGER_ACCEPTED,
      title: 'Manager Request Accepted',
      message: `${notification.fullName} accepted your request to be a manager for your organization.`,
      userId: userId,
      status: 'accepted',
      timestamp: new Date().toISOString()
    });
    
    return true;
  } catch (error) {
    console.error('Error accepting manager request:', error);
    throw error;
  }
};

/**
 * Rejects a manager request for an organization
 * @param {string} notificationId - The ID of the notification
 * @param {string} userId - The ID of the user rejecting the request
 * @returns {Promise<boolean>} Success status
 */
export const rejectManagerRequest = async (notificationId, userId) => {
  const db = getFirestoreInstance();
  
  try {
    // Get the specific notification document
    const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
    const notificationDoc = await getDoc(notificationRef);
    
    if (!notificationDoc.exists()) {
      throw new Error('Notification not found');
    }
    
    const notification = notificationDoc.data();
    
    // Get important values from notification
    const organizationId = notification.organizationId;
    
    await runTransaction(db, async (transaction) => {
      // Update organization's managedManagers subcollection
      const managerRef = doc(db, 'users', organizationId, 'managedManagers', userId);
      const managerDoc = await transaction.get(managerRef);

      // Mark the notification as rejected
      transaction.update(notificationRef, {
        status: 'rejected',
        respondedAt: serverTimestamp()
      });
     
      if (managerDoc.exists()) {
        transaction.update(managerRef, {
          status: 'rejected',
          respondedAt: serverTimestamp()
        });
      }
    });
    
    // Create notification for the organization owner after transaction completes
    await createNotification(organizationId, 'general', {
      type: NOTIFICATION_TYPES.ORGMANAGER_REJECTED,
      title: 'Manager Request Declined',
      message: `${notification.fullName} declined your request to be a manager for your organization.`,
      userId: userId,
      status: 'rejected',
      timestamp: new Date().toISOString()
    });
    
    return true;
  } catch (error) {
    console.error('Error rejecting manager request:', error);
    throw error;
  }
};


/**
 * Accepts an artist request for an organization
 * @param {string} notificationId - The ID of the notification
 * @param {string} userId - The ID of the user accepting the request
 * @returns {Promise<boolean>} Success status
 */
export const acceptArtistRequest = async (notificationId, userId) => {
  const db = getFirestoreInstance();
  
  try {
    // Get the notification data
    const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
    const notificationDoc = await getDoc(notificationRef);
    
    if (!notificationDoc.exists()) {
      throw new Error('Notification not found');
    }
    
    const notification = notificationDoc.data();
    const organizationId = notification.organizationId;
    const requestId = notification.data?.actData?.actId || notification.data?.requestId;
    
    if (!organizationId || !requestId) {
      throw new Error('Missing organization or request information');
    }
    
    // 1. Mark notification as accepted
    await updateDoc(notificationRef, {
      status: 'accepted',
      respondedAt: serverTimestamp(),
      read: true
    });
    
    // 2. Create notification for the organization
    await createNotification(organizationId, 'general', {
      type: NOTIFICATION_TYPES.ORGARTIST_ACCEPTED,
      title: 'Artist Request Accepted',
      message: `${notification.fullName} has accepted your request to manage them.`,
      userId: userId,
      status: 'accepted',
      timestamp: new Date().toISOString(),
      data: {
        _displayData: {
          userId: userId
        }
      }
    });
    
    return true;
  } catch (error) {
    console.error('Error accepting artist request:', error);
    throw error;
  }
};

/**
 * Rejects an artist request for an organization
 * @param {string} notificationId - The ID of the notification
 * @param {string} userId - The ID of the user rejecting the request
 * @returns {Promise<boolean>} Success status
 */
export const rejectArtistRequest = async (notificationId, userId) => {
  const db = getFirestoreInstance();
  
  try {
    // Get the notification data
    const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
    const notificationDoc = await getDoc(notificationRef);
    
    if (!notificationDoc.exists()) {
      throw new Error('Notification not found');
    }
    
    const notification = notificationDoc.data();
    const organizationId = notification.organizationId;
    const requestId = notification.data?.actData?.actId || notification.data?.requestId;
    
    // 1. Mark notification as rejected
    await updateDoc(notificationRef, {
      status: 'rejected',
      respondedAt: serverTimestamp(),
      read: true
    });
    
    // 2. Delete from pendingManagingActs if request ID exists
    if (organizationId && requestId) {
      try {
        const pendingActRef = doc(db, 'users', organizationId, 'pendingManagingActs', requestId);
        await deleteDoc(pendingActRef);
      } catch (error) {
        console.error('Error deleting pending request:', error);
        // Continue even if this fails
      }
    }
    
    // 3. Create notification for the organization
    await createNotification(organizationId, 'general', {
      type: NOTIFICATION_TYPES.ORGARTIST_REJECTED,
      title: 'Artist Request Declined',
      message: `${notification.fullName} has declined your request to manage them.`,
      userId: userId,
      status: 'rejected',
      timestamp: new Date().toISOString(),
      data: {
        _displayData: {
          userId: userId
        }
      }
    });
    
    return true;
  } catch (error) {
    console.error('Error rejecting artist request:', error);
    throw error;
  }
};


/**
 * Refreshes notification counts and data for a user
 * @param {string} userId - The user ID to refresh notifications for
 * @param {boolean} forceFetch - Whether to force a fresh fetch from the database
 * @returns {Promise<Object>} An object containing the refreshed notification counts
 */
export const refreshNotifications = async (userId, forceFetch = true) => {
  try {
    if (!userId) {
      console.error('User ID is required to refresh notifications');
      return { success: false, error: 'User ID is required' };
    }
    
    
    // Refresh data in local storage or cache
    await getNotifications(userId, 'general', null, 8, forceFetch);
    await getNotifications(userId, 'invitations', null, 8, forceFetch);
    
    // Get user's bands to refresh their notifications
    const db = getFirestoreInstance();
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const associatedActs = userData.associatedActs || [];
      const bands = associatedActs.filter(act => act.type === 'band');
      
      // Refresh notifications for each band
      for (const band of bands) {
        if (band.id) {
          await getBandNotifications(band.id, null, 8, forceFetch);
        }
      }
    }
    
    return {
      success: true,
    };
  } catch (error) {
    console.error('Error refreshing notifications:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Clear notification caches from localStorage
 * @returns {boolean} Success status
 */
export const clearNotificationCaches = () => {
  try {
    Object.keys(localStorage)
      .filter(key => 
        key.startsWith(CACHE_KEYS.GENERAL) || 
        key.startsWith(CACHE_KEYS.INVITATIONS) ||
        key.startsWith(CACHE_KEYS.BAND) ||
        key.startsWith(CACHE_KEYS.LAST_UPDATED) ||
        key.startsWith(RECOMMENDATION_CACHE_PREFIX)
      )
      .forEach(key => localStorage.removeItem(key));
    
    console.log('Notification caches cleared');
    return true;
  } catch (error) {
    console.error('Error clearing notification caches:', error);
    return false;
  }
};

// Export all functions and constants
export {
  sendExpoPushNotification
};